// src/App.js — Integrated Travel Tracker + Album Generator
// OPTIMIZED: Improved city resolution with GPS clustering and better concurrency
// - Base: full Past Trips app (map, steps, save, resolve, editor)
// - Integration: replaces old AlbumCreator route with new Album Generator page
// - Keeps everything else intact
// - NEW: Optimized geocoding with 80-90% fewer API calls

import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { buildPhotoObject } from './heic';
import StartScreen from './StartScreen.jsx';
import './App.css';
// ⬇️ UPDATED: Import the new function and remove the old one
import { isNativeAndroid, isNativeIOS, reverseGeocodeNativeIOS, tryNativeAlbumImport, tryNativeFolderImport } from './native-bridge';
// ⬇️ NEW: mount the album generator inside the main app
import AlbumGenerator from './pages/album-generator';
import AlbumFromPast from './album/AlbumFromPast.jsx';
import ResolvingLoader from './components/ResolvingLoader';
import AlbumGatheringLoader from './components/AlbumGatheringLoader';
import { saveTrip, getTrip, isIndexedDBSupported, getAllTrips, getAllTripsMeta } from './utils/tripStorage';
import { checkDBHealth } from './utils/photoStore';

import TripList from './components/TripList';
import { StatusBar, Style } from '@capacitor/status-bar';
import ShareViewer from "./share/ShareViewer";
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import StoryPlayerTest from './StoryPlayerTest';
import StoryViewer from "./share/StoryViewer";
import FirebaseDiagnostic from './pages/FirebaseDiagnostic'; // adjust path as needed
import DiagnosticPage from './pages/DiagnosticPage';
import ShareTestGenerator from './components/ShareTestGenerator';
import CurrentTripDigestDialog from './components/CurrentTripDigestDialog';
import { App as CapacitorApp } from '@capacitor/app'; // <-- ADD THIS IMPORT
import { Capacitor } from '@capacitor/core'; // <-- ADD THIS LINE
import vivaJourLogo from './assets/vivajour-logo-mark.png';
import heroImage from './assets/landing-hero.png';
import vivaJourWordmark from './assets/vivajour-wordmark.png';
import landingBackground from './assets/vivajour-landing-bg.png';

import { flushSync } from 'react-dom';

import LandingHome from './pages/landing/HomePage';
import PrivacyFirstPage from './pages/landing/PrivacyFirstPage';
import CurrentTripPage from './pages/landing/CurrentTripPage';
import HowItWorksPage from './pages/landing/HowItWorksPage';
import AlbumsPage from './pages/landing/AlbumsPage';
import TemplatesPage from './pages/landing/TemplatesPage';
import PrivacyPolicyPage from './pages/landing/PrivacyPolicyPage';
import TermsOfServicePage from './pages/landing/TermsOfServicePage';
import TechnicalDetailsPage from './pages/landing/TechnicalDetailsPage';

// ✅ NEW: Import subscription providers
import { AuthProvider } from './hooks/useAuth';
import { SubscriptionProvider } from './hooks/useSubscription';
// ✅ NEW: Import AccountMenu
import AccountMenu from './components/AccountMenu/AccountMenu';

import SubscriptionSuccessHandler from './components/Subscription/SubscriptionSuccessHandler';

// import './pages/landing/LandingPages.css';  // The CSS is already imported inside each component of landing pages, but if you want it loaded globally


/**
 * Travel Tracker — App.js (integrated)
 * - Keeps Phase-0 features (import, steps, likes/delete, map, save to localStorage, resolve, export)
 * - Replaces the old AlbumCreator route with the new Album Generator page
 */

// MVP: hide tabs/extra entry points for now

const MVP_MODE = true;

// 🎛️ FEATURE FLAGS - Easy on/off switches
const FEATURE_FLAGS = {
  SHOW_RESOLVING_LOADER: true,  // ← Set to false to disable the loader
  DEBUG_MODE: false,
  // 🔹 TROUBLESHOOTING: Set to true to disable flying photos animation
  // This helps isolate if jitter is caused by flying photos or something else
  DEBUG_DISABLE_FLYING_PHOTOS: false
};

// ============================================================
// 🩺 Storage Health Check Utilities
// ============================================================
// Run in browser console: window.checkStorageHealth()
// Diagnoses localStorage and IndexedDB issues for debugging

async function checkStorageHealth() {
  const results = {
    localStorage: { available: false, used: 0, quota: 5120, itemCount: 0, largeItems: [] },
    indexedDB: { available: false, healthy: false, version: null, error: null },
    recommendation: ''
  };
  
  // Check localStorage
  try {
    const test = '_storage_test_';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    results.localStorage.available = true;
    
    let total = 0;
    let itemCount = 0;
    const largeItems = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key);
        const size = (key.length + (value?.length || 0)) * 2; // UTF-16 = 2 bytes/char
        total += size;
        itemCount++;
        if (size > 100 * 1024) {
          largeItems.push({ key, sizeKB: Math.round(size / 1024) });
        }
      }
    }
    
    results.localStorage.used = Math.round(total / 1024);
    results.localStorage.itemCount = itemCount;
    results.localStorage.largeItems = largeItems.sort((a, b) => b.sizeKB - a.sizeKB);
  } catch (e) {
    results.localStorage.error = e.message;
  }
  
  // Check IndexedDB
  try {
    const health = await checkDBHealth();
    results.indexedDB = {
      available: true,
      healthy: health.healthy,
      version: health.version,
      error: health.error || null
    };
  } catch (e) {
    results.indexedDB.error = e.message;
  }
  
  // Generate recommendation
  if (!results.indexedDB.healthy) {
    results.recommendation = '⚠️ IndexedDB unhealthy. Try: DevTools > Application > IndexedDB > Delete "VivaJourPhotos" then reload';
  } else if (results.localStorage.used > 4000) {
    results.recommendation = '⚠️ localStorage nearly full (' + results.localStorage.used + 'KB/5120KB). Run window.cleanupLargeLocalStorage()';
  } else if (results.localStorage.largeItems.length > 0) {
    results.recommendation = '⚠️ Found ' + results.localStorage.largeItems.length + ' large localStorage items. Run window.cleanupLargeLocalStorage()';
  } else {
    results.recommendation = '✅ Storage healthy';
  }
  
  // Pretty print results
  console.log('\n📊 STORAGE HEALTH REPORT');
  console.log('========================');
  console.log('\n📁 localStorage:');
  console.log('   Available:', results.localStorage.available);
  console.log('   Used:', results.localStorage.used, 'KB /', results.localStorage.quota, 'KB');
  console.log('   Items:', results.localStorage.itemCount);
  if (results.localStorage.largeItems.length > 0) {
    console.log('   ⚠️ Large items (>100KB):');
    results.localStorage.largeItems.forEach(item => {
      console.log('      -', item.key, ':', item.sizeKB, 'KB');
    });
  }
  console.log('\n💾 IndexedDB:');
  console.log('   Available:', results.indexedDB.available);
  console.log('   Healthy:', results.indexedDB.healthy);
  console.log('   Version:', results.indexedDB.version);
  if (results.indexedDB.error) {
    console.log('   ❌ Error:', results.indexedDB.error);
  }
  console.log('\n💡 Recommendation:', results.recommendation);
  console.log('');
  
  return results;
}

// 🧹 Manual cleanup utility - run: window.cleanupLargeLocalStorage()
function cleanupLargeLocalStorage(dryRun = false) {
  console.log(dryRun ? '\n🔍 DRY RUN - Scanning...\n' : '\n🧹 Cleaning up large localStorage items...\n');
  
  const keysToRemove = [];
  let totalFreed = 0;
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    
    const value = localStorage.getItem(key);
    const sizeKB = ((key.length + (value?.length || 0)) * 2) / 1024;
    
    const isAlbumData = key.startsWith('vvd:album:item:') || 
                        key.startsWith('vvd:lastAlbum') ||
                        key.startsWith('vvd:albumCache:') ||
                        key === 'vvd:lastAlbumPages';
    
    if (isAlbumData && sizeKB > 100) {
      console.log(`  ${dryRun ? 'Would remove' : 'Removing'}: ${key} (${Math.round(sizeKB)}KB)`);
      keysToRemove.push(key);
      totalFreed += sizeKB;
    }
  }
  
  if (!dryRun) {
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }
  
  if (keysToRemove.length > 0) {
    console.log(`\n${dryRun ? 'Would free' : 'Freed'}: ${Math.round(totalFreed)}KB from ${keysToRemove.length} items`);
  } else {
    console.log('No large album items found in localStorage');
  }
  
  return { removed: keysToRemove.length, freedKB: Math.round(totalFreed) };
}

// Expose utilities to window for console access
if (typeof window !== 'undefined') {
  window.checkStorageHealth = checkStorageHealth;
  window.cleanupLargeLocalStorage = cleanupLargeLocalStorage;
}

// ------------------------------ Helpers ------------------------------

// ------------------------------ Helpers ------------------------------


const isFiniteNum = (n) => typeof n === 'number' && Number.isFinite(n);
const isValidCoord = (c) => !!c && isFiniteNum(c.lat) && isFiniteNum(c.lng)
  && c.lat >= -90 && c.lat <= 90 && c.lng >= -180 && c.lng <= 180;

function toISODate(d) {
  try {
    if (!d) return '';
    console.log('[DATE DEBUG] App.js toISODate input:', d, 'type:', typeof d);
    
    // If already a YYYY-MM-DD string, return it directly (avoid re-parsing which causes timezone issues)
    if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
      console.log('[DATE DEBUG] App.js toISODate returning string directly:', d);
      return d;
    }
    const dt = new Date(d);
    if (isNaN(dt.getTime())) {
      console.log('[DATE DEBUG] App.js toISODate invalid date');
      return '';
    }
    // Use LOCAL date components instead of UTC to avoid day shift
    const year = dt.getFullYear();
    const month = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    const result = `${year}-${month}-${day}`;
    console.log('[DATE DEBUG] App.js toISODate parsed result:', result, 'from Date:', dt.toString());
    return result;
  } catch (e) { 
    console.log('[DATE DEBUG] App.js toISODate error:', e);
    return ''; 
  }
}
function prettyDate(dstr) {
  if (!dstr) return '';
  // Parse as LOCAL midnight, not UTC (removed the 'Z')
  const d = new Date(dstr + 'T00:00:00');
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
function formatUSDate(dstr) {
  if (!dstr) return '';
  const [y, m, d] = dstr.split('-').map(Number);
  if (!y || !m || !d) return dstr;
  return `${String(m).padStart(2,'0')}/${String(d).padStart(2,'0')}/${y}`;
}
function roundToGrid(x, size = 0.25) { return Math.round(x / size) * size; }

function formatPlace(addr) {
  if (!addr) return '';
  
  // City-level location
  const city = addr.city || addr.town || addr.village || addr.municipality || '';
  
  // State/territory (Puerto Rico, Hawaii, etc.)
  const state = addr.state || addr.territory || '';
  
  // Region/district (neighborhood level)
  const region =
    addr.neighbourhood || addr.suburb || addr.quarter || addr.city_district ||
    addr.district || addr.state_district || addr.county || addr.region || '';
    
  const country = addr.country || addr.country_name || (addr.country_code ? addr.country_code.toUpperCase() : '');
  
  // Build parts from most specific to least specific
  const parts = [];
  
  // Add region if we have it and it's different from city
  if (region && region !== city && region !== state) {
    parts.push(region);
  }
  
  // Add city
  if (city) {
    parts.push(city);
  }
  
  // Add state/territory if different from city (important for Puerto Rico)
  if (state && state !== city) {
    parts.push(state);
  }
  
  // Add country
  if (country) {
    parts.push(country);
  }
  
  return parts.join(', ');
}
function stepHeaderPlace(step) {
  if (step && step.location && String(step.location).trim()) return String(step.location).trim();
  const p = (step.photos || []).find(ph => ph.address);
  const formatted = formatPlace(p?.address || step.address || null);
  return formatted || 'Unknown location';
}
function cityFromAddress(addr) {
  if (!addr) return '';
  return (
    addr.city || addr.town || addr.village || addr.municipality ||
    addr.hamlet || addr.suburb || addr.city_district || ''
  );
}

// country first, then state/region, then city - always one line
function formatOneLineLocation(step) {
  // PRIORITY 1: If user manually edited the location field, use that
  if (step?.location && step.location.trim() !== '') {
    return step.location.trim();
  }
  
  // PRIORITY 2: Otherwise, auto-generate from address
  const addr = step?.address || {};
  const country =
    addr.country || addr.country_name || step.country || step.countryName || '';
  
  // State/territory (important for places like Puerto Rico, Hawaii, Alaska, etc.)
  const state = addr.state || addr.territory || addr.region || addr.state_district || '';
  
  // City-level location
  const city =
    addr.city || addr.town || addr.locality || addr.village || addr.municipality || '';
  
  if (!country && !state && !city) return '';
  
  // Build location string with available parts
  // For US territories like Puerto Rico, we want: "Puerto Rico, USA" or "San Juan, Puerto Rico, USA"
  const parts = [];
  if (city) parts.push(city);
  if (state && state !== city) parts.push(state); // Avoid "San Juan, San Juan"
  if (country) parts.push(country);
  
  // Format: "City, State, Country" or "State, Country" or just "Country"
  return parts.join(', ');
}

// human date label (reuse what you already have if present)
function formatStepDate(step) {
  // if you already have a formatter, use it; this is a safe fallback:
  const iso = step?.date || step?.day || step?.dateTaken;
  if (!iso) return '';
  try {
    // Parse as LOCAL midnight, not UTC (append T00:00:00 without Z)
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString(undefined, { year:'numeric', month:'long', day:'numeric' });
  } catch { return String(iso); }
}


function firstCoord(step) {
  const p = (step.photos || []).find(ph => isValidCoord(ph?.coordinates));
  return p?.coordinates || null;
}

// Selection helpers
function evenSpreadPick(arr, k) {
  if (k >= arr.length) return [...arr];
  const out = [];
  for (let i = 0; i < k; i++) {
    const idx = Math.round(i * (arr.length - 1) / Math.max(1, k - 1));
    out.push(arr[idx]);
  }
  const uniq = Array.from(new Set(out));
  while (uniq.length < k) {
    const r = arr[Math.floor(Math.random() * arr.length)];
    if (!uniq.includes(r)) uniq.push(r);
  }
  return uniq;
}
function applySelection(steps, { limitEnabled, limitCount, strategy }) {
  if (!limitEnabled || !limitCount || limitCount <= 0) return steps;
  return steps.map(s => {
    const arr = [...(s.photos || [])];
    let pick = [];
    if (strategy === 'likes') {
      arr.sort((a, b) => (b.likes || 0) - (a.likes || 0));
      pick = arr.slice(0, limitCount);
    } else if (strategy === 'diverse') {
      pick = evenSpreadPick(arr, limitCount);
    } else {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      pick = arr.slice(0, limitCount);
    }
    return { ...s, photos: pick };
  });
}

// ------------------------------ Grouping ------------------------------
function stepKeyForPhoto(photo) {
  console.log('[DATE DEBUG] stepKeyForPhoto photo.dateTaken:', photo.dateTaken, 'fileName:', photo.fileName);
  const date = toISODate(photo.dateTaken) || toISODate(new Date());
  console.log('[DATE DEBUG] stepKeyForPhoto resolved date:', date);
  const c = isValidCoord(photo.coordinates) ? photo.coordinates : null;
  const grid = c ? `${roundToGrid(c.lat)}:${roundToGrid(c.lng)}` : 'nogps';
  return `${date}|${grid}`;
}
function addPhotoIntoSteps(steps, photo) {
  const key = stepKeyForPhoto(photo);
  const idx = steps.findIndex(s => s._key === key);
  if (idx === -1) {
    return [
      ...steps,
      {
        _key: key,
        id: key,
        date: toISODate(photo.dateTaken) || toISODate(new Date()),
        title: '',
        location: '',
        description: '',
        address: null,
        photos: [photo],
      },
    ];
  }
  const next = [...steps];
  next[idx] = { ...next[idx], photos: [...(next[idx].photos || []), photo] };
  return next;
}


// ------------------------------ Reverse Geocoding ------------------------------
// iOS: CLGeocoder throttles at ~50 reverse-geocode requests / 60 seconds.
// To avoid the "timeUntilReset ~52s" penalty, we hard-cap below that.
const IOS_GEOCODE_MAX_PER_MIN = 47;
const IOS_GEOCODE_WINDOW_MS = 60_000;

// We serialize access so concurrent calls cannot "stampede" past the limit.
let iosGeocodeGate = Promise.resolve();
let iosGeocodeTimestamps = [];

function sleep(ms) {
  return new Promise(res => setTimeout(res, ms));
}

async function awaitIOSGeocodeSlot() {
  if (!(isNativeIOS && isNativeIOS())) return;

  iosGeocodeGate = iosGeocodeGate.then(async () => {
    const now = Date.now();

    // Drop timestamps older than our rolling window
    iosGeocodeTimestamps = iosGeocodeTimestamps.filter(t => (now - t) < IOS_GEOCODE_WINDOW_MS);

    // If we are at/over limit, wait until the oldest slot exits the window
    if (iosGeocodeTimestamps.length >= IOS_GEOCODE_MAX_PER_MIN) {
      const oldest = iosGeocodeTimestamps[0];
      const waitMs = Math.max(0, IOS_GEOCODE_WINDOW_MS - (now - oldest) + 25); // +25ms safety margin
      await sleep(waitMs);

      // Recompute after waiting
      const now2 = Date.now();
      iosGeocodeTimestamps = iosGeocodeTimestamps.filter(t => (now2 - t) < IOS_GEOCODE_WINDOW_MS);
    }

    // Consume a slot
    iosGeocodeTimestamps.push(Date.now());
  });

  return iosGeocodeGate;
}


async function reverseGeocode(lat, lon) {
    // iOS native path first (Android/web unchanged)
  if (isNativeIOS && isNativeIOS()) {
    try {
      await awaitIOSGeocodeSlot();
const nativeAddr = await reverseGeocodeNativeIOS(lat, lon);

      if (nativeAddr) {
        console.log('[Geocode] ✅ iOS native geocoder used');
        return nativeAddr;
      }
      console.log('[Geocode] iOS native returned null; falling back to Nominatim');
    } catch (e) {
      console.warn('[Geocode] iOS native threw; falling back to Nominatim:', e?.message || e);
    }
  }

  const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}&zoom=14&addressdetails=1`;
  try {
    const resp = await fetch(url, { headers: { 'Accept': 'application/json' } });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const data = await resp.json();
    return data?.address || null;
  } catch (e) {
    console.warn('reverseGeocode error', e);
    return null;
  }
}
async function reverseCountryOnly(lat, lon) {
  const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}&zoom=3&addressdetails=1`;
  try {
    const resp = await fetch(url, { headers: { 'Accept': 'application/json' } });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const data = await resp.json();
    const a = data?.address || {};
    // Include state/territory - crucial for US territories like Puerto Rico, Guam, etc.
    return { 
      country: a.country || '', 
      country_code: a.country_code || '',
      state: a.state || a.territory || a.region || '',  // Capture state/territory info
    };
  } catch (e) {
    console.warn('reverseCountryOnly error', e);
    return null;
  }
}

async function geocode(query) {
  const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=jsonv2&limit=1&addressdetails=1`;
  try {
    const resp = await fetch(url, { headers: { 'Accept': 'application/json' } });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const data = await resp.json();
    if (data && data.length > 0) {
      const result = data[0];
      // Return the full result so we can get lat, lon, and address
      return { 
        lat: result.lat, 
        lon: result.lon, 
        address: result.address 
      };
    }
    return null;
  } catch (e) {
    console.warn('geocode error', e);
    return null;
  }
}

// NEW HELPER: From the reverted fix (App.js, line 1055)
// This is needed by the new resolver.
export async function maybeAttachLiveGPS(photo) {
  if (photo?.gpsExtracted) return photo;
  if (!('geolocation' in navigator)) return photo;
  
  try {
    const pos = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 8000,
        maximumAge: 0,
      });
    });
    
    const { latitude, longitude } = pos.coords || {};
    if (typeof latitude === 'number' && typeof longitude === 'number') {
      return { 
        ...photo, 
        gpsExtracted: true, 
        coordinates: { lat: latitude, lng: longitude } 
      };
    }
  } catch {}
  
  return photo;
}

// NEW HELPER: Re-created from handoff doc
// This was missing from the reverted fix, causing the "not defined" error.
function buildClusters(photos, gridSize = 0.5) {
  const clusters = new Map();
  const round = (x) => Math.round(x / gridSize) * gridSize;
  
  photos.forEach(photo => {
    const c = photo.coordinates;
    // Only cluster photos that have valid coordinates
    if (c && typeof c.lat === 'number' && typeof c.lng === 'number') {
      const lat = round(c.lat);
const lng = round(c.lng);
const key = `${lat.toFixed(2)},${lng.toFixed(2)}`;

      if (!clusters.has(key)) {
        clusters.set(key, {
          representative: c,
          photos: []
        });
      }
      clusters.get(key).photos.push(photo);
    }
  });
  return clusters;
}

// ------------------------------ NEW: Optimized City Resolution ------------------------------

// NEW: Cluster photos by GPS coordinates before geocoding
function clusterPhotosByLocation(rawSteps) {
  const clusters = new Map();
  
  rawSteps.forEach((step, stepIdx) => {
    (step.photos || []).forEach((photo, photoIdx) => {
      const c = photo?.coordinates;
      const addr = photo?.address || step?.address || null;
      const hasGPS = c && isFiniteNum(c.lat) && isFiniteNum(c.lng);
      const hasCountry = !!(addr?.country || addr?.country_name || addr?.country_code);
      const hasCity = !!(addr?.city || addr?.town || addr?.village || addr?.municipality);
      
      if (hasGPS && hasCountry && !hasCity) {
        // Use larger grid for clustering (0.01 degrees ≈ 1km) to reduce API calls
        const clusterLat = Math.round(c.lat / 0.01) * 0.01;
        const clusterLng = Math.round(c.lng / 0.01) * 0.01;
        const clusterKey = `${clusterLat.toFixed(2)},${clusterLng.toFixed(2)}`;
        
        if (!clusters.has(clusterKey)) {
          clusters.set(clusterKey, {
            lat: clusterLat,
            lng: clusterLng,
            items: [],
            representative: { lat: c.lat, lng: c.lng } // Use first photo's exact coords
          });
        }
        
        clusters.get(clusterKey).items.push({ stepIdx, photoIdx });
      }
    });
  });
  
  console.log(`City resolution: ${rawSteps.flatMap(s => s.photos || []).length} photos clustered into ${clusters.size} locations`);
  return clusters;
}

// NEW: Concurrent worker pool for processing clusters
async function poolMap(items, workerFn, maxConcurrency, onProgress) {
  if (!items || items.length === 0) {
    if (onProgress) onProgress(1); // optional: treat as complete
    return [];
  }

  let i = 0;
  let done = 0;
  const results = new Array(items.length);
  let active = 0;

  return new Promise((resolve) => {
    function processNext() {
      while (active < maxConcurrency && i < items.length) {
        const currentIndex = i++;
        active++;

        workerFn(items[currentIndex], currentIndex)
          .then(result => { results[currentIndex] = result; })
          .catch(error => { results[currentIndex] = { error: error.message || String(error) }; })
          .finally(() => {
            active--;
            done++;

            if (onProgress) onProgress(done / items.length);

            if (done === items.length) resolve(results);
            else processNext();
          });
      }
    }

    processNext();
  });
}


// ------------------------------ Map helpers ------------------------------
function FitToSteps({ steps }) {
  const map = useMap();
  useEffect(() => {
    const pts = steps.map(firstCoord).filter(Boolean);
    if (!pts.length) return;
    const bounds = L.latLngBounds(pts.map(c => [c.lat, c.lng]));
    try { map.fitBounds(bounds.pad(0.2), { animate: true }); } catch {}
  }, [steps, map]);
  return null;
}

// Drop-pin icon (SVG)
const pinSvg = encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
    <path d="M16 1C9.925 1 5 5.925 5 12c0 7.5 9 18 11 18s11-10.5 11-18C27 5.925 22.075 1 16 1z" fill="#ef4444" stroke="#111827" stroke-width="1.25"/>
    <circle cx="16" cy="12" r="4" fill="#fff" stroke="#111827" stroke-width="1.25"/>
  </svg>
`);
const pinIcon = L.icon({
  iconUrl: `data:image/svg+xml;utf8,${pinSvg}`,
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -26],
});

// ------------------------------ UI: Map ------------------------------
function MapPanel({ steps, onSelectStep }) {
  const pts = steps.map(firstCoord).filter(Boolean);
  const center = pts.length ? [pts[0].lat, pts[0].lng] : [20, 0];
  return (
    <div className="card map-panel">
      <div className="map-head">
        <div className="title">Map</div>
      </div>
      <MapContainer center={center} zoom={3} style={{ height: 360, borderRadius: 12 }}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <FitToSteps steps={steps} />
        {steps.map((s, i) => {
          const c = firstCoord(s);
          if (!c) return null;
          return (
            <Marker key={s.id || i} position={[c.lat, c.lng]} icon={pinIcon} eventHandlers={{ click: () => onSelectStep(i) }}>
              <Popup>
                <div style={{ fontWeight: 700 }}>{stepHeaderPlace(s) || 'Step'}</div>
                <div style={{ fontSize: 12 }}>{prettyDate(s.date)}</div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}

function MapFullscreenModal({ open, onClose, steps, onSelectStep }) {
  if (!open) return null;
  const pts = steps.map(firstCoord).filter(Boolean);
  const center = pts.length ? [pts[0].lat, pts[0].lng] : [20, 0];
  return (
    <div className="modal">
      <div className="modal-card" style={{ width: 'min(1200px, 96vw)', height: '80vh' }}>
        <div className="modal-head">
          <div className="title">Map</div>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
        <div style={{ flex: 1 }}>
          <MapContainer center={center} zoom={3} style={{ height: '100%', borderRadius: 12 }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitToSteps steps={steps} />
            {steps.map((s, i) => {
              const c = firstCoord(s);
              if (!c) return null;
              return (
                <Marker key={s.id || i} position={[c.lat, c.lng]} icon={pinIcon} eventHandlers={{ click: () => onSelectStep(i) }}>
                  <Popup>
                    <div style={{ fontWeight: 700 }}>{stepHeaderPlace(s) || 'Step'}</div>
                    <div style={{ fontSize: 12 }}>{prettyDate(s.date)}</div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </div>
      </div>
    </div>
  );
}

// ------------------------------ UI: Album Wizard ------------------------------
function AlbumWizardModal({ open, onClose, onCreate }) {
  const [mode, setMode] = useState('auto');
  if (!open) return null;
  return (
    <div className="modal">
      <div className="modal-card">
        <div className="modal-head">
          <div className="title">Create album</div>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
        <div className="grid2">
          <div className="col">
            <label>Mode
              <select value={mode} onChange={e => setMode(e.target.value)}>
                <option value="auto">Auto (random within steps)</option>
                <option value="manual">Manual (pick later)</option>
              </select>
            </label>
          </div>
          <p style={{ marginTop: 8, fontSize: 13, opacity: .8 }}>
            Auto mode will pick a balanced set across your steps. Manual lets you curate later.
          </p>
        </div>
        <div className="actions">
          <button className="btn btn-cancel" onClick={onClose}>Cancel</button>
          {/* Now navigates into the Album Generator page */}
          <button className="btn btn-primary" onClick={() => { onCreate?.(mode); onClose(); }}>
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

// 🔹 NEW: My Albums modal (reads from vvd:album:list in localStorage)
// 🔹 NEW: My Albums modal (Updated with Grid Layout & Delete)
function MyAlbumsModal({ open, onClose, onOpenAlbum }) {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(false);

  // Helper: Reload albums from DB (FAST - uses metadata first, then loads full data for displayed albums)
  const loadAlbums = async () => {
    if (!open) return;
    try {
      if (!isIndexedDBSupported()) {
        setAlbums([]);
        return;
      }
      setLoading(true);
      
      // 🔹 UPDATED: Use fast metadata loading first
      const tripsMeta = await getAllTripsMeta();
      
      // Filter to trips that have SAVED albums (not drafts)
      const tripsWithSavedAlbums = tripsMeta.filter(t => t.hasAlbum && t.albumSaved);
      
      // For each, load full trip to get album details (but only for the filtered list - much smaller)
      const albumsWithDetails = await Promise.all(
        tripsWithSavedAlbums.slice(0, 20).map(async (meta) => { // Limit to 20 most recent
          try {
            const fullTrip = await getTrip(meta.id);
            if (!fullTrip?.albumData) return null;
            
            const a = fullTrip.albumData;
            const createdAt = a.createdAt || a.savedAt || fullTrip.updatedAt || fullTrip.savedAt || Date.now();

            // Build a label for the selection mode
            let selectionLabel = 'Album';
            if (a.selectionMode === 'diverse') selectionLabel = 'Diverse';
            else if (a.selectionMode === 'vibes') selectionLabel = 'Vibes';
            else if (a.digest) selectionLabel = 'Digest';

            // Try to find a cover photo
            let coverUrl = null;
            if (a.pages && a.pages.length > 0) {
              const coverPage = a.pages.find(p => p.templateKey === 'cover' || p.type === 'cover');
              if (coverPage && coverPage.photos?.[0]?.url) {
                coverUrl = coverPage.photos[0].url;
              } else {
                for (const p of a.pages) {
                  if (p.photos?.[0]?.url) {
                    coverUrl = p.photos[0].url;
                    break;
                  }
                }
              }
            }

            return {
              id: fullTrip.id,
              title: a.title || fullTrip.name || 'Untitled album',
              createdAt,
              selectionLabel,
              photoCount: a.pages ? a.pages.reduce((acc, p) => acc + (p.photos?.length || 0), 0) : 0,
              coverUrl,
              trip: fullTrip,
              albumData: a,
            };
          } catch (e) {
            console.warn('[MyAlbumsModal] Failed to load trip:', meta.id, e);
            return null;
          }
        })
      );

      const validAlbums = albumsWithDetails
        .filter(Boolean)
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

      setAlbums(validAlbums);
    } catch (e) {
      console.warn('[MyAlbumsModal] Failed to load albums:', e);
      setAlbums([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAlbums();
  }, [open]);

  // Handle Delete Album (removes albumData from trip, keeps trip)
  const handleDeleteAlbum = async (e, albumItem) => {
    e.stopPropagation(); // Stop click from opening the album
    
    if (!window.confirm(`Delete the album "${albumItem.title}"?\n(The trip and photos will remain, only the created album layout will be deleted.)`)) {
      return;
    }

    try {
      // 1. Create a trip object WITHOUT albumData
      const updatedTrip = { ...albumItem.trip };
      delete updatedTrip.albumData; // Remove the album payload
      updatedTrip.updatedAt = Date.now();

      // 2. Save back to DB
      await saveTrip(updatedTrip);

      // 3. Remove from local list immediately
      setAlbums(prev => prev.filter(a => a.id !== albumItem.id));
      
    } catch (err) {
      console.error('Failed to delete album:', err);
      alert('Could not delete album.');
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/80 z-[9999] backdrop-blur-sm p-4">
      <div className="bg-slate-900 text-slate-100 rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col border border-slate-700">
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-slate-800/50 rounded-t-xl">
          <h2 className="text-xl font-bold">My Albums</h2>
          <button
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {/* Content Area */}
        <div className="p-6 overflow-y-auto min-h-[300px]">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-48 text-slate-400 gap-3">
              <div className="w-6 h-6 border-2 border-slate-500 border-t-transparent rounded-full animate-spin" />
              Loading albums…
            </div>
          ) : albums.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-slate-500">
              <div className="text-4xl mb-2">📷</div>
              <p>No albums created yet.</p>
              <p className="text-xs mt-2 opacity-60">Go to a Past Trip to create one!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {albums.map((album) => (
                <div
                  key={album.id}
                  onClick={() => onOpenAlbum && onOpenAlbum(album)}
                  className="group bg-slate-800 rounded-lg border border-slate-700 overflow-hidden cursor-pointer hover:border-slate-500 transition-all shadow-lg hover:shadow-xl flex flex-col"
                >
                  {/* Thumbnail / Cover Image */}
                  <div className="aspect-video bg-slate-700 relative overflow-hidden">
                    {album.coverUrl ? (
                      <img 
                        src={album.coverUrl} 
                        alt="Album cover" 
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-slate-500">
                        {/* Camera Placeholder Icon */}
                        <svg className="w-12 h-12 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                    )}
                    
                    {/* Badge */}
                    <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full backdrop-blur-md">
                      {album.selectionLabel}
                    </div>
                  </div>

                  {/* Info Body */}
                  <div className="p-3 text-center flex-1 flex flex-col justify-center gap-1">
                    <h3 className="font-bold text-slate-100 truncate w-full" title={album.title}>
                      {album.title}
                    </h3>
                    <div className="text-xs text-slate-400">
                      {album.photoCount} photos
                    </div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wide">
                      {new Date(album.createdAt).toLocaleDateString(undefined, { month:'short', day:'numeric', year:'numeric' })}
                    </div>
                  </div>

                  {/* Footer Action (Delete) */}
                  <div className="border-t border-slate-700/50 p-2 flex justify-end bg-slate-800/80">
                    <button
                      onClick={(e) => handleDeleteAlbum(e, album)}
                      className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded transition-colors"
                      title="Delete Album"
                    >
                      {/* Trash Icon */}
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ------------------------------ UI: Step Editor ------------------------------
function StepEditorModal({ open, step, onChange, onClose }) {
  // ENHANCEMENT: Pre-populate location field with computed value if empty
  const initDraft = useCallback((s) => {
    if (!s) return null;
    
    // If location field is empty, populate it with the computed location string
    // This is what the user sees in the UI (e.g., "Croatia · Mlini")
    let location = s.location || '';
    if (!location || location.trim() === '') {
      location = formatOneLineLocation(s);
    }
    
    return {
      ...s,
      location // Use the computed location if the field was empty
    };
  }, []);
  
  const [draft, setDraft] = useState(initDraft(step));
  const photos = draft?.photos || [];
  const [idx, setIdx] = useState(0);
  const active = photos[idx];

  const stripRef = useRef(null);
  const gotoPrev = useCallback(() => setIdx((p) => (p - 1 + photos.length) % photos.length), [photos.length]);
  const gotoNext = useCallback(() => setIdx((p) => (p + 1) % photos.length), [photos.length]);

  useEffect(() => { setDraft(initDraft(step)); setIdx(0); }, [step, initDraft]);

  useEffect(() => {
    if (!open) return;
    const el = stripRef.current?.querySelector('.strip-thumb.active');
    if (el) el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [idx, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); gotoPrev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); gotoNext(); }
      if (e.key === 'Escape') { e.preventDefault(); onClose?.(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, gotoPrev, gotoNext, onClose]);

  if (!open || !draft) return null;

  const handleRemovePhoto = (i) =>
    setDraft(d => ({ ...d, photos: (d.photos || []).filter((_, k) => k !== i) }));

  return (
    <div className="modal">
      <div className="modal-card">
        <div className="modal-head">
          <div className="title">Edit step</div>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>

        <div className="modal-body">
          <div className="grid2">
            <div className="col">
              
              <label>Location
                <input value={draft.location || ''} onChange={e => setDraft(d => ({ ...d, location: e.target.value }))} />
              </label>
              <label>Date
                <input type="date" value={draft.date || ''} onChange={e => setDraft(d => ({ ...d, date: e.target.value }))} />
              </label>
              <label>Description
                <textarea rows={5} value={draft.description || ''} onChange={e => setDraft(d => ({ ...d, description: e.target.value }))} />
              </label>
            </div>

            {active && (
              <div className="editor-preview" style={{ marginBottom: 10 }}>
                <button className="nav prev" onClick={gotoPrev} aria-label="Previous">‹</button>
                <img src={active.url} alt={active.fileName || 'preview'} />
                <button
  className={`preview-like ${active?.likes > 0 ? 'is-liked' : ''}`}
  onClick={() => setDraft(d => {
    const arr = [...(d.photos || [])];
    const cur = { ...(arr[idx] || {}) };
    const liked = (cur.likes || 0) > 0;
    cur.likes = liked ? 0 : 1;     // ← toggle 1 ↔ 0
    cur.deleted = false;           // ← ensure not deleted when liked
    arr[idx] = cur;
    return { ...d, photos: arr };
  })}
  aria-label={active?.likes > 0 ? 'Unlike current' : 'Like current'}
  title={active?.likes > 0 ? 'Unlike' : 'Like'}
>
  {active?.likes > 0 ? '❤ Liked' : '♡ Like'}
</button>

                <button className="nav next" onClick={gotoNext} aria-label="Next">›</button>
                <button className="preview-delete" onClick={() => handleRemovePhoto(idx)} aria-label="Remove current">×</button>
              </div>
            )}

            <div className="edit-strip">
              <button className="strip-nav" onClick={gotoPrev}>‹</button>
              <div className="strip-viewport" ref={stripRef}>
                <div className="strip-row">
                  {photos.map((p, i) => (
                    <button
                      key={i}
                      className={`strip-thumb ${i === idx ? 'active' : ''}`}
                      onClick={() => setIdx(i)}
                      title={p.fileName || 'photo'}
                    >
                      <img src={p.url} alt={p.fileName || 'thumbnail'} />
                    </button>
                  ))}
                </div>
              </div>
              <button className="strip-nav" onClick={gotoNext}>›</button>
            </div>
          </div>
        </div>

        <div className="actions">
          <button className="btn btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onChange(draft)}>Save changes</button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------ Photo identity + grid ------------------------------
function photoSignature(p) {
  if (!p) return '';
  if (p.url) return p.url;
  const of = p.originalFile || {};
  if (of.name && of.size && of.lastModified) return `${of.name}::${of.size}::${of.lastModified}`;
  return JSON.stringify([p.fileName || '', p.size || 0, p.lastModified || 0]);
}

function StepPhotosGrid({ photos, collapsed, onLike, onDelete, onVisibleCount }) {
  const gridRef = useRef(null);
  const [cols, setCols] = useState(0);

  useEffect(() => {
    if (!gridRef.current) return;
    const update = () => {
  if (!gridRef.current) return;  // ← Added this null check
  const cs = getComputedStyle(gridRef.current);
      const colsCount = (cs.gridTemplateColumns || '').split(' ').filter(Boolean).length || 1;
      setCols(colsCount);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(gridRef.current);
    return () => ro.disconnect();
  }, []);

  const visible = collapsed ? photos.slice(0, Math.max(1, cols) * 2) : photos;

  useEffect(() => {
    if (typeof onVisibleCount === 'function') onVisibleCount(visible.length);
  }, [visible.length, onVisibleCount]);

  return (
    <div className="photo-grid small" ref={gridRef}>
      {visible.map((p, j) => (
        <div
  className={`photo ${p.deleted ? 'photo--deleted' : ''}`}
  key={p.url || j}
  style={{ position: 'relative' }}
>
  <img src={p.url} alt={p.fileName || 'photo'} title={p.fileName || ''} />

  {/* ❌ overlay when deleted */}
  {p.deleted && <div className="photo-x-overlay">✕</div>}

  <div className="photo-actions">
    <button
      className={`icon ${p?.likes > 0 ? 'is-liked' : ''}`}
      title={p?.likes > 0 ? 'Unlike' : 'Like'}
      aria-pressed={p?.likes > 0}
      onClick={() => onLike(p)}
    >
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path
          d="M12.1 21.35l-1.45-1.32C6 15.36 3.5 13.03 3.5 9.9c0-2.2 1.79-4 4-4 1.54 0 3.04.9 3.7 2.24C11.66 6.8 13.16 5.9 14.7 5.9c2.21 0 4 1.8 4 4 0 3.13-2.5 5.46-7.15 10.13l-1.45 1.32z"
          style={{
            fill: p?.likes > 0 ? '#ef4444' : 'none',
            stroke: p?.likes > 0 ? '#ef4444' : '#9ca3af',
            strokeWidth: 2
          }}
        />
      </svg>
    </button>

    {/* 🗑 toggles delete on/off (your onDelete already wired) */}
    <button className="icon" title={p.deleted ? 'Restore' : 'Delete'} onClick={() => onDelete(p)}>🗑</button>
  </div>
</div>

      ))}
    </div>
  );
}

// ------------------------------ Trip save helpers ------------------------------
function slugifyName(name) {
  return (name || 'untitled')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 64);
}

// thin snapshot for saving
function thinPhotoForSave(p = {}) {
  return {
    url: p.url || '',
    fileName: p.fileName || '',
    dateTaken: p.dateTaken || null,
    likes: p.likes || 0,
    coordinates: p.coordinates ? { lat: p.coordinates.lat, lng: p.coordinates.lng } : null,
    address: p.address ? {
      country: p.address.country || p.address.country_name || '',
      country_code: p.address.country_code || '',
      city: p.address.city || p.address.town || p.address.village || ''
    } : null
  };
}
function thinStepForSave(s = {}) {
  return {
    _key: s._key || s.id || '',
    id: s.id || s._key || '',
    date: s.date || '',
    title: s.title || '',
    location: s.location || '',
    description: s.description || '',
    address: s.address || null,
    photos: (s.photos || []).map(thinPhotoForSave)
  };
}
function makeTripPayload(name, steps) {
  return {
    version: 1,
    name: name || 'Untitled trip',
    steps: (steps || []).map(thinStepForSave),
    savedAt: new Date().toISOString(),
  };
}
function safeLocalSet(key, value) {
  try { localStorage.setItem(key, value); return true; }
  catch (e) { console.warn('localStorage set failed', e); return false; }
}
function updateTripIndex(key) {
  try {
    const idxKey = 'tt:trip:index';
    const cur = JSON.parse(localStorage.getItem(idxKey) || '[]');
    if (!cur.includes(key)) {
      cur.push(key);
      localStorage.setItem(idxKey, JSON.stringify(cur));
    }
  } catch {}
}
function downloadBackup(filename, jsonText) {
  try {
    const blob = new Blob([jsonText], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  } catch {}
}

function StepHeaderCompact({
  step,
  onEdit,        // pass your existing edit handler
  onShowAll,     // pass your existing show-all handler
  EditButton,    // optionally pass your styled buttons instead of handlers
  ShowAllButton,
}) {
  const location = formatOneLineLocation(step);
  const dateText = formatStepDate(step);

  return (
    <div
      className="step-header-compact"
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'start',
        gap: 12,
        marginBottom: 8,
      }}
    >
      {/* Left: now three fixed rows (Location, Date, Count) */}
<div style={{ minWidth: 0 }}>
  {/* City, Country (title) */}
  <div
    title={location}
    style={{
      fontWeight: 700,
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }}
  >
    {location || '\u00A0'}
  </div>

  {/* Date */}
  <div
    style={{
      opacity: 0.8,
      fontSize: 13,
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }}
  >
    {dateText || '\u00A0'}
  </div>

  {/* Photo count */}
  <div
    style={{
      opacity: 0.75,
      fontSize: 12,
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }}
  >
    {(() => {
      const n = (step?.photos?.length || 0);
      return `${n} ${n === 1 ? 'photo' : 'photos'}`;
    })()}
  </div>
</div>


      {/* Right: Edit on top, Show all underneath */}
      <div
        style={{
          display: 'grid',
          gridAutoRows: 'min-content',
          gap: 6,
          justifyItems: 'end',
        }}
      >
        {EditButton ? (
          EditButton
        ) : (
          <button className="btn btn-ghost" onClick={onEdit}>Edit</button>
        )}
        {ShowAllButton ? (
          ShowAllButton
        ) : (
          <button className="btn" onClick={onShowAll}>Show all</button>
        )}
      </div>
    </div>
  );
}

// --- Step 0 (Home) helpers ---
function hasHomeStep(arr) {
  return Array.isArray(arr) && arr.length > 0 && arr[0]?.isHome === true;
}

function FAQContent() {
  return (
    <div style={{ fontSize: 14, lineHeight: 1.5 }}>
      <h1 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>
        VivaJour – Frequently Asked Questions (FAQ)
      </h1>
      <p style={{ marginBottom: 16, opacity: 0.8 }}>
        A smart way to rediscover your memories.
      </p>

      {/* 1. General Questions */}
      <h2 style={{ fontSize: 16, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>
        1. General Questions
      </h2>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>What is VivaJour?</p>
      <p style={{ marginBottom: 8 }}>
        VivaJour automatically organizes your photos by date and location, builds a beautiful travel album, and lets you
        export, share, or print your journey — all in minutes.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Who is VivaJour for?</p>
      <p style={{ marginBottom: 8 }}>
        Travelers, families, students, creatives, and anyone with thousands of photos and no time to organize them.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Do I need an account to use the app?</p>
      <p style={{ marginBottom: 12 }}>
        No. Everything works locally on your device. An optional cloud-sync feature may come in the future.
      </p>

      {/* 2. Importing & Photo Processing */}
      <h2 style={{ fontSize: 16, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>
        2. Importing &amp; Photo Processing
      </h2>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>How does VivaJour organize my photos?</p>
      <p style={{ marginBottom: 8 }}>
        The app reads the EXIF metadata in your photos (date, time, GPS). It uses this information to group photos into
        places, days, and natural chapters of your trip.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>What if some photos do not have location data?</p>
      <p style={{ marginBottom: 8 }}>
        VivaJour still groups them intelligently using date/time patterns, neighboring photos, filename similarities,
        and smart clustering.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Does the app upload my photos anywhere?</p>
      <p style={{ marginBottom: 8 }}>
        No. Photos never leave your device. EXIF extraction, smart grouping, chapter analysis, layout building,
        Smart Vibes AI, PDF export, and Story Preview all run locally on your phone or browser.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Why do some photos take a few seconds to load?</p>
      <p style={{ marginBottom: 12 }}>
        High-resolution photos require decoding, especially on Android Chrome. This is normal and does not affect your
        originals.
      </p>

      {/* 3. Creating Albums */}
      <h2 style={{ fontSize: 16, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>
        3. Creating Albums (Past Trips vs Current Trips)
      </h2>

      <p style={{ marginBottom: 8 }}>
        VivaJour supports two distinct album creation flows: <strong>Past Trips</strong> for importing older photos, and{' '}
        <strong>Current Trips</strong> for trips that are happening right now.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Past Trips – importing older photos</p>
      <p style={{ marginBottom: 8 }}>
        For Past Trips, you import photos (optionally choose a date range), VivaJour analyzes them, and you can
        optionally use the <strong>Edit Chapters</strong> screen before generating the album.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Current Trips – albums for an ongoing trip</p>
      <p style={{ marginBottom: 8 }}>
        For Current Trips, you select one or more days, and VivaJour immediately generates an album with a sensible
        default selection. You can still edit and improve the album afterwards (change photos, layouts, captions, etc.).
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Does VivaJour run in the background?</p>
      <p style={{ marginBottom: 12 }}>
        No. VivaJour does <strong>not</strong> track you or run in the background. It only analyzes photos when you open
        the app and generate an album, so it does not silently drain your battery.
      </p>

      {/* 4. Edit Chapters (✓ and X) */}
      <h2 style={{ fontSize: 16, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>
        4. Edit Chapters (✓ and X)
      </h2>

      <p style={{ marginBottom: 8 }}>
        The <strong>Edit Chapters</strong> screen appears for Past Trips and lets you fine-tune which photos are used.
        This step is optional, but it can greatly improve the final album.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>What does ✓ (like) do?</p>
      <p style={{ marginBottom: 8 }}>
        ✓ guarantees that a photo will appear somewhere in the album and gives it higher priority in the layout. Use it
        for your favorite shots that you absolutely want to see.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>What does X (remove) do?</p>
      <p style={{ marginBottom: 8 }}>
        X removes that photo from the album so it will not be used on any page. Use it for blurry images, duplicates,
        random screenshots, or anything you do not want included.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>What if I do nothing on this screen?</p>
      <p style={{ marginBottom: 12 }}>
        You can skip the Edit Chapters step entirely. VivaJour will automatically select a strong set of photos based on
        time, place, and Smart Vibes scoring.
      </p>

      {/* 5. Smart Features */}
      <h2 style={{ fontSize: 16, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>
        5. Smart Features
      </h2>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>What is Smart Vibes?</p>
      <p style={{ marginBottom: 8 }}>
        Smart Vibes is VivaJour’s AI system that scores photos based on their “vibe.” The app currently uses four
        primary vibe categories in the UI: Scenic, People, Urban, and Nature. These scores help build well-balanced
        pages and story collages.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Does the AI run online or offline?</p>
      <p style={{ marginBottom: 8 }}>
        100% offline. Your photos are not sent to any external server for analysis.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Does VivaJour detect faces?</p>
      <p style={{ marginBottom: 12 }}>
        Yes. VivaJour uses on-device face detection to improve vibe scoring, prioritize meaningful people-focused photos,
        and suggest better cover images. Face data is never stored or uploaded.
      </p>

      {/* 6. File Formats & Compatibility */}
      <h2 style={{ fontSize: 16, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>
        6. File Formats &amp; Compatibility
      </h2>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>What photo formats are supported?</p>
      <p style={{ marginBottom: 8 }}>
        VivaJour supports HEIC / HEIF (iPhone photos), JPEG / JPG, and PNG.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Is there a photo import limit?</p>
      <p style={{ marginBottom: 8 }}>
        There is no artificial limit. VivaJour can process hundreds to thousands of photos, depending on your device’s
        memory.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Does VivaJour bypass Android’s 100-photo selection limit?</p>
      <p style={{ marginBottom: 8 }}>
        Yes. A custom Android picker allows you to select thousands of photos at once, bypassing the usual 100-photo
        limit.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Which platforms are supported?</p>
      <p style={{ marginBottom: 12 }}>
        VivaJour runs as an Android app and as a web app (Chrome, Safari, Firefox, Edge). An iOS version is planned for
        the future.
      </p>

      {/* 7. Location Display & Naming */}
      <h2 style={{ fontSize: 16, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>
        7. Location Display &amp; Naming
      </h2>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>How does VivaJour determine location names?</p>
      <p style={{ marginBottom: 8 }}>
        VivaJour uses reverse geocoding to translate GPS coordinates into real-world place names like “Old Town,
        Dubrovnik”, “Brooklyn Bridge, NYC”, or “Plitvice Lakes, Croatia”.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Can I edit chapter names?</p>
      <p style={{ marginBottom: 8 }}>
        Yes. Chapter titles can be edited so you can rename places or days in a way that fits your story.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Does it work internationally?</p>
      <p style={{ marginBottom: 8 }}>
        Yes. VivaJour supports location naming worldwide, as long as GPS data is available.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>What if no location is detected?</p>
      <p style={{ marginBottom: 12 }}>
        If a photo has no GPS data, VivaJour still places it based on the date/time sequence. You can manually update
        the chapter names, and your changes will be reflected in the album.
      </p>

      {/* 8. Story Preview & Sharing */}
      <h2 style={{ fontSize: 16, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>
        8. Story Preview &amp; Sharing
      </h2>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>What is the Story Preview?</p>
      <p style={{ marginBottom: 8 }}>
        Story Preview is an animated, flipbook-style version of your album, ideal for reliving or sharing your journey.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Can I share my album?</p>
      <p style={{ marginBottom: 8 }}>
        Yes. You can generate a shareable story experience that loads quickly on most devices.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Do recipients see full-resolution photos?</p>
      <p style={{ marginBottom: 12 }}>
        No. Shared stories use optimized images so they can load fast and work well on mobile networks.
      </p>

      {/* 9. Saving & Storage */}
      <h2 style={{ fontSize: 16, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>
        9. Saving &amp; Storage
      </h2>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Are albums saved automatically?</p>
      <p style={{ marginBottom: 8 }}>
        Yes. Both Trips and Albums autosave continuously so you do not lose your work.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Where are my albums stored?</p>
      <p style={{ marginBottom: 8 }}>
        All data is stored locally on your device. Nothing is uploaded to a centralized server by VivaJour.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>How much space do albums take?</p>
      <p style={{ marginBottom: 12 }}>
        Albums are optimized. Even large trips usually take less than about 50 MB of storage.
      </p>

      {/* 10. Printing & PDF Export */}
      <h2 style={{ fontSize: 16, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>
        10. Printing &amp; PDF Export
      </h2>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>What is the difference between Normal PDF and Print-Quality PDF?</p>
      <p style={{ marginBottom: 8 }}>
        Normal PDF is available now and includes a small VivaJour watermark. Print-Quality PDF (higher resolution,
        no watermark) is planned for a future update.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Why does the Normal PDF include a watermark?</p>
      <p style={{ marginBottom: 8 }}>
        The watermark distinguishes standard PDF export from the future premium print-quality export.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Can I print the Normal PDF?</p>
      <p style={{ marginBottom: 12 }}>
        Yes. The Normal PDF is designed to be printable, but it will include a subtle VivaJour watermark.
      </p>

      {/* 11. Troubleshooting */}
      <h2 style={{ fontSize: 16, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>
        11. Troubleshooting
      </h2>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>My photos are grouped incorrectly.</p>
      <p style={{ marginBottom: 8 }}>
        This can happen when photos come from multiple devices or timezones. Try adjusting the date range,
        or re-importing with a narrower selection.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>What if photos have the wrong timezone?</p>
      <p style={{ marginBottom: 8 }}>
        If photos were taken on different devices or in different timezones, you may see some grouping drift. VivaJour
        still attempts to cluster photos as intelligently as possible.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>The preview looks cut off on Android.</p>
      <p style={{ marginBottom: 8 }}>
        Make sure you are running the latest version of the app. Layout scaling and preview behavior are updated over
        time.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>The PDF looks blurry.</p>
      <p style={{ marginBottom: 12 }}>
        This can happen with low-resolution originals. A higher-resolution, print-quality export is planned for a
        future version.
      </p>

      {/* 12. Album Management */}
      <h2 style={{ fontSize: 16, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>
        12. Album Management
      </h2>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>How do I delete a trip or album?</p>
      <p style={{ marginBottom: 8 }}>
        Go to <strong>My Trips</strong> or <strong>My Albums</strong>, select the item you want to remove, and choose
        Delete.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Can I duplicate an album?</p>
      <p style={{ marginBottom: 12 }}>
        Currently, duplication is done by regenerating an album from the same trip. A direct “Duplicate Album” option
        is planned.
      </p>

      {/* 13. Performance & Tips */}
      <h2 style={{ fontSize: 16, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>
        13. Performance, Battery &amp; Tips
      </h2>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>How long does analysis take?</p>
      <p style={{ marginBottom: 8 }}>
        Times vary by device, but as a rough guide: around 20–40 seconds for ~100 photos, 1–2 minutes for ~300 photos,
        3–5 minutes for ~1,000 photos, and longer for larger trips. HEIC photos on Android may take slightly longer due
        to decoding.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Does VivaJour use a lot of battery?</p>
      <p style={{ marginBottom: 8 }}>
        No. Analysis uses short processing bursts, similar to opening your camera or a photo editor, and the app does
        not run in the background.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Do I have to keep the app open for Current Trips?</p>
      <p style={{ marginBottom: 8 }}>
        No. VivaJour does not run in the background or continuously monitor your trip. You only consume battery when you
        are actively generating or editing an album.
      </p>

      <p style={{ fontWeight: 600, marginBottom: 4 }}>Any tips for best results?</p>
      <p style={{ marginBottom: 12 }}>
        Keep location services on while traveling, use ✓ for favorite photos, use X to remove unwanted shots, add
        captions or journal notes for context, and import one trip at a time when possible for the cleanest grouping.
      </p>

      <p style={{ marginTop: 16, fontSize: 12, opacity: 0.7 }}>
        Still stuck? In future versions you&apos;ll be able to contact support directly from this screen.
      </p>
    </div>
  );
}

// ------------------------------ Main App ------------------------------
function AppContent() {
  // Android native picker

// Check if we need to show permission hint on Android
// This is now only used as a fallback when native request fails
const shouldShowPermissionHint = () => {
  if (!isNativeAndroid()) return false;
  // Now this is controlled by the permission request result, not localStorage
  return false; // Will be set dynamically based on permission result
};

const markPermissionHintSeen = () => {
  localStorage.setItem('vvd:hasSeenPhotoPermissionHint', 'true');
};

// ------------------------------
// File type guards (prevents video/unknown files from stalling import)
// ------------------------------
const isProbablyVideoName = (name = '') =>
  /\.(mp4|mov|m4v|avi|mkv|webm|3gp|3gpp|mts|m2ts)$/i.test(String(name || ''));

const isSupportedImageFile = (fileLike) => {
  const name = String(fileLike?.name || fileLike?.fileName || '');
  const type = String(fileLike?.type || fileLike?.mimeType || '').toLowerCase();

  // Explicit video mime-type or common video extensions => skip
  if (type.startsWith('video/')) return false;
  if (isProbablyVideoName(name)) return false;

  // Accept common image types + HEIC/HEIF by extension or mime
  const isImageType = type.startsWith('image/');
  const isImageExt = /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(name);

  return isImageType || isImageExt;
};

const isSupportedNativeAsset = (p) => {
  if (!isSupportedImageFile(p)) return false;
  // Native assets should have at least one usable path/URL field
  return !!(p?.url || p?.src || p?.webPath || p?.path || p?.uri);
};


  const handleNativePick = async (directConfig = null) => {
    if (!isNativeAndroid() && !isNativeIOS()) return false;
    try {

      console.log('[native] calling tryNativeAlbumImport');

      // 🔹 NEW: If this is a Current Trip digest, calculate the date range to pass to native picker
      // Use directConfig if provided (for immediate calls), otherwise use state
          const config =
      directConfig ||
      (pendingDigestAfterImport && digestMode === 'current'
        ? pendingDigestConfig
        : null);

    let dateFromMs = null;
    let dateToMs = null;

    if (config?.selectedDates && config.selectedDates.length) {
      const sorted = config.selectedDates.filter(Boolean).sort();
      const start = new Date(sorted[0]);
      start.setHours(0, 0, 0, 0);
      const end = new Date(sorted[sorted.length - 1]);
      end.setHours(23, 59, 59, 999);

      dateFromMs = start.getTime();
      dateToMs = end.getTime();

      console.log(
        `[native] Passing date range to picker: ${start.toISOString()} to ${end.toISOString()} (${sorted.length} day(s) selected)`
      );
    } else if (config?.days) {
      // Backwards-compat: if anything still passes "days"
      const days = config.days;
      const today = new Date();
      today.setHours(23, 59, 59, 999); // End of today
      dateToMs = today.getTime();

      const startDate = new Date(today);
      startDate.setDate(startDate.getDate() - (days - 1));
      startDate.setHours(0, 0, 0, 0); // Start of that day
      dateFromMs = startDate.getTime();

      console.log(
        `[native] Passing date range to picker: ${new Date(
          dateFromMs
        ).toISOString()} to ${new Date(dateToMs).toISOString()} (${days} days)`
      );
    }

    // --- PAST TRIPS: show loader BEFORE awaiting the native picker result (iOS lag fix) ---
    // Determine flow BEFORE the await (Current Trip already has its own gathering loader)
    const isCurrentTripDigestFlowPre =
      digestFlowActive ||
      (digestMode === 'current' && pendingDigestAfterImport) ||
      !!directConfig;

    if (!isCurrentTripDigestFlowPre && FEATURE_FLAGS.SHOW_RESOLVING_LOADER) {
      setSuppressLoader(false);
      setLoaderContext('import');
      setShowResolvingLoader(true);
      setResolvingProgress(0);
      setResolvingStatus('Preparing photos...');
      importInProgressRef.current = true;

      // Let React commit + WKWebView paint BEFORE the native promise blocks JS again
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    }

    // 1. Await the native picker result
    // 🔹 Date filtering is now handled safely in native-bridge.js for both platforms
    const nativePhotos = await tryNativeAlbumImport({
      dateFromMs,
      dateToMs,
    });

    console.log(
      '[native] result count =',
      Array.isArray(nativePhotos) ? nativePhotos.length : nativePhotos
    );

    // 2. Handle Cancellation / Empty Return IMMEDIATELY
    if (!nativePhotos || !Array.isArray(nativePhotos) || nativePhotos.length === 0) {
      // If we pre-showed a loader for Past Trips, clean it up
      if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER && !isCurrentTripDigestFlowPre) {
        setShowResolvingLoader(false);
        setLoaderContext(null);
        setResolvingProgress(0);
        setResolvingStatus('');
        importInProgressRef.current = false;
      }
      return false;
    }

    // 3. SURGICAL FIX: Trigger Loader State IMMEDIATELY (BEFORE filtering)
    // -----------------------------------------------------------------------
    // 🛠️ FIX: Force detection of Current Trip flow using directConfig
    // This bypasses stale closures where digestMode might be null
    // -----------------------------------------------------------------------
    const isCurrentTripDigestFlow = digestFlowActive || 
                                   (digestMode === 'current' && pendingDigestAfterImport) || 
                                   !!directConfig;

    if (isCurrentTripDigestFlow) {
      console.log('[native] Current Trip digest flow - using GatheringLoader only');
      setSuppressLoader(true);           // Block ResolvingLoader everywhere
      setShowResolvingLoader(false);     // Make sure it's hidden
      setDigestFlowActive(true);         // Ensure digest flow is active
      setShowGatheringLoader(true);      // Show loader BEFORE heavy processing
      setLoaderContext('import');
    } else if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER) {
      // 👉 PAST TRIP: Turn on loader NOW (BEFORE filtering)
      setSuppressLoader(false);
      setLoaderContext('import');
      setShowResolvingLoader(true);
      setResolvingProgress(0);
      setResolvingStatus('Preparing photos...');
      importInProgressRef.current = true;
    }

    // 4. CRITICAL FIX: Force UI Paint with guaranteed frame completion
    // -----------------------------------------------------------------------
    // iOS WebView batches paint operations more aggressively than Android/Web.
    // We need to:
    //   a) Let React flush state updates
    //   b) Wait for the browser's next animation frame (ensures DOM is updated)
    //   c) Wait for a second frame (ensures pixels are actually painted)
    //   d) Add extra buffer time for iOS's slower WebView rendering
    // -----------------------------------------------------------------------
    const waitForPaint = () => new Promise(resolve => {
      // First rAF: DOM updates are scheduled
      requestAnimationFrame(() => {
        // Second rAF: DOM updates are applied, paint is scheduled  
        requestAnimationFrame(() => {
          resolve();
        });
      });
    });
    
    // Wait for paint, then add platform-specific buffer
    await waitForPaint();
    const isIOSPlatform = typeof isNativeIOS === 'function' && isNativeIOS();
    // iOS needs longer buffer (300ms) because WKWebView compositor is slower
    // Android/Web can use shorter buffer (100ms)
    const bufferMs = isIOSPlatform ? 300 : 100;
    await new Promise(r => setTimeout(r, bufferMs));

    // 5. Resume Heavy Processing - NOW do the filtering AFTER loader is visible
    cancelResolveRef.current = false;
    
    const filteredNativePhotos = nativePhotos.filter(isSupportedNativeAsset);

    const skipped = nativePhotos.length - filteredNativePhotos.length;
    if (skipped > 0) {
      console.log(`[native] Skipping ${skipped} non-image/unsupported item(s) (likely videos).`);
    }

    if (!filteredNativePhotos.length) {
      console.warn('[native] No supported images returned from native picker.');
      // Clean up loader states since we're returning early
      setShowResolvingLoader(false);
      setShowGatheringLoader(false);
      setLoaderContext(null);
      return false;
    }

    // 🛠️ FIX: For Current Trip, NOW feed photos to loader (after filtering)
    if (isCurrentTripDigestFlow) {
      // 🔹 DEBUG: If flying photos are disabled, keep photos empty to test if they cause jitter
      if (FEATURE_FLAGS.DEBUG_DISABLE_FLYING_PHOTOS) {
        console.log(`[native] DEBUG: Flying photos disabled, keeping gatheringPhotos empty`);
        // Don't set photos - loader will animate without flying photos
      } else {
        console.log(`[native] Feeding ${filteredNativePhotos.length} photos to gathering animation`);
        setGatheringPhotos(filteredNativePhotos.slice(0, 80)); // Cap at 80 for performance
      }
    }

    if (cancelResolveRef.current) return false;

      setProgress({ total: filteredNativePhotos.length, done: 0 });
      let done = 0;
      let merged = [...rawSteps];

      // 🔹 CHANGE 2: Throttle UI updates to avoid iOS re-render thrash
      let lastUiTick = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      const isIOSDevice = typeof isNativeIOS === 'function' && isNativeIOS();

      for (const p of filteredNativePhotos) {
        try {
          p.likes = p.likes || 0;
          merged = addPhotoIntoSteps(merged, p);
        } finally {
          done += 1;

          const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
          const isLast = done === filteredNativePhotos.length;
          const everyN = (done % 20 === 0);
          const timeElapsed = (now - lastUiTick) >= 200;
          
          // iOS: Yield more aggressively in the first few photos to ensure loader is visible
          const isEarlyBatch = done <= 5;

          // Only update UI every 20 photos, every 200ms, or on last photo
          if (everyN || timeElapsed || isLast || (isIOSDevice && isEarlyBatch)) {
            lastUiTick = now;

            setProgress({ total: filteredNativePhotos.length, done });

            if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER) {
              const importProgress = Math.round((done / filteredNativePhotos.length) * 40);
              setResolvingProgress(importProgress);
              setResolvingStatus(`Importing photos… ${done}/${filteredNativePhotos.length}`);
            }

            // Give iOS a chance to paint occasionally during long imports
            // Use requestAnimationFrame for more reliable yielding than setTimeout(0)
            if (!isLast && isIOSDevice) {
              await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 0)));
            }
          }
        }
      }
      setRawSteps(merged);
      
      // 🔹 FIX: Mark import as just completed - gives geocodingInProgress time to update
      importCompletedAtRef.current = Date.now();
      importInProgressRef.current = false; // Import finished

      // 🔹 NEW: Auto-save immediately using the merged data
      performAutoSave(merged);

// If this import was kicked off by the Current Trip button,
// auto-generate the album now that photos are in memory.
// Use directConfig if provided (for immediate calls), otherwise check state
const shouldAutoGenerate = directConfig || (pendingDigestAfterImport && digestMode === 'current' && pendingDigestConfig);

if (shouldAutoGenerate) {
  // 🔹 Photos already fed to GatheringLoader at line 1715
  // Just ensure loader states are still active and call handleGenerateDailyDigest
  setDigestFlowActive(true);
  setShowGatheringLoader(true);

  // 🛠️ FIX: Explicitly set this HERE to prevent flicker during view transition.
  // We cannot rely on handleGenerateDailyDigest to do it because of stale closures.
  setAwaitingAlbumReady(true); 
  
  setAwaitingAlbumReadySource("digest");
  // Use directConfig if provided, otherwise use state
  const configToUse = directConfig || pendingDigestConfig;
  
  if (configToUse) {
    handleGenerateDailyDigest({
      ...configToUse,
      stepsOverride: merged,
    });
    
    // Clear state flags if using state-based flow
    if (!directConfig) {
      setPendingDigestAfterImport(false);
      setPendingDigestConfig(null);
      // NOTE: Do NOT reset digestMode here - it's needed for isGatheringActive
      // digestMode will be reset when onAlbumReady completes or user cancels
    }
  }
}


      setTimeout(() => setProgress({ total: 0, done: 0 }), 600);
      return true;
    } catch (e) {
      // The native album rejects on cancel, so we can ignore it and not show an error.
      if (e?.message?.toLowerCase() !== 'user cancelled') {
        console.error('Native album pick failed', e);
      }
      
      // 🔹 Reset ALL loader states on cancel/error
      if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER) {
        setShowResolvingLoader(false);
        setLoaderContext(null);
        setResolvingProgress(0);
        setResolvingStatus('');
        importInProgressRef.current = false; // 🔹 FIX: Clear import flag
      }
      // 🔹 Also reset gathering loader on cancel
      setShowGatheringLoader(false);
      setDigestFlowActive(false);
      setGatheringPhotos([]);
      
      return false;
    }
  };
// Unified pick handler: uses native album on Android, web picker otherwise
// TEMPORARILY DISABLED - unused function
/*
const onPickPhotos = async () => {
  try {
    if (isNativeAndroid()) {
      const photos = await tryNativeAlbumImport({ maxCount: 2000 });
      if (photos?.length) {
        console.log('Native photos:', photos.length);
      }
      return;
    }
    await handleWebPick();
  } catch (err) {
    if (String(err?.message || '').toLowerCase().includes('cancel')) return;
    console.error('onPickPhotos failed:', err);
  }
};
*/


  const handleFolderImport = async () => {
  let photos = [];
  
  try {
    // Try native Android first
    if (isNativeAndroid()) {
      const nativePhotos = await tryNativeFolderImport();
      if (!nativePhotos || !nativePhotos.length) {
        return; // User cancelled
      }
      photos = nativePhotos;
    } 
    // Modern browsers: Use File System Access API
    else if ('showDirectoryPicker' in window) {
      try {
        // Request directory access
        const directoryHandle = await window.showDirectoryPicker();
        
        // Show loader
        if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER) {
          setSuppressLoader(false);         // NEW
          setLoaderContext('import'); // NEW
          setShowResolvingLoader(true);
          setResolvingProgress(0);
          setResolvingStatus('Scanning folder...');
        }
        
        // Collect file handles (don't read files yet - just scan names)
        const fileHandles = [];
        for await (const entry of directoryHandle.values()) {
          if (entry.kind === 'file') {
            const name = entry.name.toLowerCase();
            // Quick filename check - don't call getFile() yet
            if (name.endsWith('.jpg') || name.endsWith('.jpeg') || 
                name.endsWith('.png') || name.endsWith('.gif') || 
                name.endsWith('.webp') || name.endsWith('.heic') || 
                name.endsWith('.heif')) {
              fileHandles.push(entry);
            }
          }
        }
        
        if (fileHandles.length === 0) {
          alert('No photos found in selected folder');
          if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER) {
            setShowResolvingLoader(false);
          }
          return;
        }
        
        // Now process files - read once and build photo objects
        // Use same pattern as onFilesSelected for consistency
        setProgress({ total: fileHandles.length, done: 0 });
        let done = 0;
        const processed = [];
        
        for (let i = 0; i < fileHandles.length; i++) {
          try {
            const file = await fileHandles[i].getFile(); // Read file once
            const photo = await buildPhotoObject(file);
            if (photo) {
              processed.push(photo);
            }
          } catch (err) {
            console.warn('Failed to process file:', fileHandles[i].name, err);
          } finally {
            done += 1;
            setProgress({ total: fileHandles.length, done });
            
            // Update progress
            if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER) {
              const scanProgress = Math.round((done / fileHandles.length) * 20);
              setResolvingProgress(scanProgress);
              setResolvingStatus(`Processing photos... ${done}/${fileHandles.length}`);
            }
          }
        }
        
        photos = processed;
      } catch (err) {
        if (err.name === 'AbortError') {
          // User cancelled the picker
          if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER) {
            setShowResolvingLoader(false);
          }
          return;
        }
        throw err; // Re-throw other errors
      }
    }
    // Fallback for Safari/Firefox: Use webkitdirectory
    else {
      // Handle everything inside Promise, then return early
      await new Promise((resolve) => {
        // Create hidden directory input
        const input = document.createElement('input');
        input.type = 'file';
        input.webkitdirectory = true;
        input.multiple = true;
        input.accept = 'image/*,.heic,.heif';
        input.style.display = 'none';
        
        input.onchange = async (e) => {
          const files = Array.from(e.target.files || []);
          
          if (files.length === 0) {
            resolve();
            return;
          }
          
          // Show loader
          if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER) {
            setSuppressLoader(false);         // NEW
            setLoaderContext('import'); // NEW
            setShowResolvingLoader(true);
            setResolvingProgress(0);
            setResolvingStatus('Scanning folder...');
          }
          
          // Process files
          const processed = [];
          for (let i = 0; i < files.length; i++) {
            try {
              const photo = await buildPhotoObject(files[i]);
              if (photo) {
                processed.push(photo);
              }
            } catch (err) {
              console.warn('Failed to process file:', files[i].name, err);
            }
            
            // Update scanning progress
            if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER) {
              const scanProgress = Math.round(((i + 1) / files.length) * 20);
              setResolvingProgress(scanProgress);
              setResolvingStatus(`Scanning folder... ${i + 1}/${files.length}`);
            }
          }
          
          document.body.removeChild(input);
          
          // Import photos (do it once, here)
          await importPhotosToSteps(processed);
          resolve();
        };
        
        input.oncancel = () => {
          document.body.removeChild(input);
          if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER) {
            setShowResolvingLoader(false);
          }
          resolve();
        };
        
        document.body.appendChild(input);
        input.click();
      });
      
      // Early return - already imported above
      return;
    }

    // Import photos (only for native/showDirectoryPicker paths)
    if (photos && photos.length > 0) {
      await importPhotosToSteps(photos);
    }
    
  } catch (e) {
    console.error('Folder import failed', e);
    alert('Folder import failed. Please try again.');
    if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER) {
      setShowResolvingLoader(false);
      importInProgressRef.current = false; // 🔹 FIX: Clear import flag
    }
  }
  
  // Helper function to import photos into steps
  async function importPhotosToSteps(photos) {
    if (!photos || photos.length === 0) {
      if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER) {
        setShowResolvingLoader(false);
        importInProgressRef.current = false; // 🔹 FIX: Clear import flag
      }
      return;
    }

    // Show loader for import phase
    if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER) {
      setResolvingProgress(20);
      setResolvingStatus('Importing photos...');
    }

    setProgress({ total: photos.length, done: 0 });
    let done = 0;
    let merged = [...rawSteps];
    
    for (const p of photos) {
      try {
        p.likes = p.likes || 0;
        merged = addPhotoIntoSteps(merged, p);
      } finally {
        done += 1;
        setProgress({ total: photos.length, done });
        
        // Update loader progress (20-60% for import)
        if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER) {
          const importProgress = 20 + Math.round((done / photos.length) * 40);
          setResolvingProgress(importProgress);
          setResolvingStatus(`Importing photos... ${done}/${photos.length}`);
        }
      }
    }
    
    setRawSteps(merged);

    // 🔹 NEW: Auto-save immediately after import
    performAutoSave(merged);

    
    setTimeout(() => setProgress({ total: 0, done: 0 }), 600);
  }
};


  const [view, setView] = useState('start');
  const [tripName, setTripName] = useState('');
  const [isNameLocked, setIsNameLocked] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const [showTripList, setShowTripList] = useState(false);
  const [showAlbumList, setShowAlbumList] = useState(false);
const [currentTripId, setCurrentTripId] = useState(null);

  const [rawSteps, setRawSteps] = useState([]);
  // 🔹 NEW: Create a ref to track steps without closure staleness
const rawStepsRef = useRef(rawSteps);
useEffect(() => { rawStepsRef.current = rawSteps; }, [rawSteps]);

  const [fromDate, setFromDate] = useState('');
  const [toDate,   setToDate]   = useState('');

  const [dateFilterEnabled, setDateFilterEnabled] = useState(false);

  const [limitEnabled, setLimitEnabled] = useState(false);
  const [limitCount,   setLimitCount]   = useState(6);
  const [selectionStrategy, setSelectionStrategy] = useState('diverse');

  const [albumOpen, setAlbumOpen] = useState(false);
  const [editing,   setEditing]   = useState(null);

  const [albumFromPastOpen, setAlbumFromPastOpen] = useState(false);
  const [showDigestDialog, setShowDigestDialog] = useState(false);
  const [pendingDigestAfterImport, setPendingDigestAfterImport] = useState(false);

  const [digestMode, setDigestMode] = useState(null);          // 'current' for Current Trip flow
const [pendingDigestConfig, setPendingDigestConfig] = useState(null); // { days, journalText }
const [digestMeta, setDigestMeta] = useState(null);
// 🔹 NEW: Track the specific album data we want to load into the overlay
const [loadedAlbumData, setLoadedAlbumData] = useState(null);

// 🔹 Auto-save tracking for imported photos (for future features)
const [isAutoSaved, setIsAutoSaved] = useState(false);
const [autoSavedId, setAutoSavedId] = useState(null);

// Permission hint modal removed - system handles permissions natively



// 🔹 One-time migration: Mark existing albums as "saved" so they appear in My Albums
useEffect(() => {
  const migrateOldAlbums = async () => {
    const migrated = localStorage.getItem('vvd:albumsMigrated:v1');
    if (migrated) return;
    
    try {
      console.log('[Migration] Checking for old albums to migrate...');
      const meta = await getAllTripsMeta();
      let migratedCount = 0;
      
      for (const t of meta) {
        // If trip has album but albumSaved is not explicitly true, migrate it
        if (t.hasAlbum && !t.albumSaved) {
          const trip = await getTrip(t.id);
          if (trip?.albumData) {
            trip.albumData.isSaved = true;
            trip.albumData.isDraft = false;
            await saveTrip(trip);
            migratedCount++;
          }
        }
      }
      
      localStorage.setItem('vvd:albumsMigrated:v1', 'true');
      if (migratedCount > 0) {
        console.log(`[Migration] ✓ Migrated ${migratedCount} existing albums`);
      }
    } catch (e) {
      console.warn('[Migration] Failed:', e);
    }
  };
  
  migrateOldAlbums();
}, []);

// 🧹 One-time cleanup: Remove large localStorage album data (migrated to IndexedDB)
// This runs once per device to ensure clean migration to IndexedDB storage
useEffect(() => {
  const cleanupDone = localStorage.getItem('vvd:localStorageCleanup:v2');
  if (cleanupDone) return;
  
  try {
    let cleaned = 0;
    let freedBytes = 0;
    const keysToRemove = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      
      // Target old album storage patterns that should now be in IndexedDB
      const isOldAlbumData = key.startsWith('vvd:album:item:') ||
                             key === 'vvd:lastAlbumPages' ||
                             key.startsWith('vvd:albumCache:');
      
      if (isOldAlbumData) {
        const value = localStorage.getItem(key);
        if (value && value.length > 500000) { // > ~500KB
          keysToRemove.push(key);
          freedBytes += value.length * 2;
          cleaned++;
        }
      }
    }
    
    // Remove in separate loop to avoid iterator issues
    keysToRemove.forEach(key => {
      console.log(`[Storage Cleanup] Removing large localStorage item: ${key} (${Math.round(localStorage.getItem(key)?.length / 1024)}KB)`);
      localStorage.removeItem(key);
    });
    
    if (cleaned > 0) {
      console.log(`[Storage Cleanup] ✅ Removed ${cleaned} large album items (~${Math.round(freedBytes / 1024)}KB freed)`);
      console.log('[Storage Cleanup] Albums are now stored in IndexedDB for better performance');
    }
    
    localStorage.setItem('vvd:localStorageCleanup:v2', 'true');
  } catch (err) {
    console.warn('[Storage Cleanup] Non-critical error:', err);
    // Still mark as done to avoid repeated attempts
    localStorage.setItem('vvd:localStorageCleanup:v2', 'true');
  }
}, []);

// App.js (around line 527)
// 🔹 UPDATED: Check if there is *any* unsaved work
// 🔹 Restore state AND trip when returning from story/share routes
useEffect(() => {
  console.log('[AppContent] Mounted, checking for return context...');
  
  const restoreState = async () => {
    try {
      const savedContext = sessionStorage.getItem('vvd:returnContext');
      
      if (!savedContext) {
        console.log('[AppContent] No saved context found');
        return;
      }
      
      const context = JSON.parse(savedContext);
      console.log('[AppContent] Found saved context:', context);
      
      // Check if context is recent (within last 5 minutes)
      const age = Date.now() - (context.timestamp || 0);
      const isRecent = age < 5 * 60 * 1000;
      
      if (!isRecent) {
        console.log('[AppContent] ⚠️  Context expired, ignoring');
        sessionStorage.removeItem('vvd:returnContext');
        sessionStorage.removeItem('vvd:currentTripId');
        return;
      }
      
      console.log('[AppContent] ✅ Context is valid, restoring...');
      
      // 🔹 STEP 1: Restore trip data from IndexedDB if we have a tripId
      // 🔹 STEP 1: Restore trip data from IndexedDB if we have a tripId
if (context.currentTripId) {
  console.log('[AppContent] Loading trip from IndexedDB:', context.currentTripId);
  try {
    const trip = await getTrip(context.currentTripId);
    if (trip) {
      console.log('[AppContent] ✅ Trip loaded from storage:', trip.name);
      setTripName(trip.name || '');
      setRawSteps(trip.steps || []);
      setCurrentTripId(trip.id);
      setLastSavedAt(trip.savedAt);
      setIsNameLocked(true);

      // 🔹 NEW: Restore album data for this trip, if any
      if (trip.albumData) {
        console.log('[AppContent] Restoring albumData from trip record');
        setLoadedAlbumData(trip.albumData);
      } else {
        // Optional: try a per-trip session fallback if IndexedDB has no album yet
        try {
          const cacheKey = `vvd:lastAlbumData:${trip.id}`;
          const cached = sessionStorage.getItem(cacheKey);
          if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed && parsed.pages && parsed.pages.length) {
              console.log(
                '[AppContent] Restoring albumData from session cache for trip',
                trip.id,
              );
              setLoadedAlbumData(parsed);
            } else {
              setLoadedAlbumData(null);
            }
          } else {
            setLoadedAlbumData(null);
          }
        } catch (err) {
          console.warn(
            '[AppContent] Failed to restore album from session cache:',
            err,
          );
          setLoadedAlbumData(null);
        }
      }
    } else {
      console.warn('[AppContent] ⚠️  Trip not found in storage');
    }
  } catch (e) {
    console.error('[AppContent] Failed to load trip from storage:', e);
  }
} else {
  console.log('[AppContent] No tripId in context, skipping trip load');
}

      
      // 🔹 STEP 2: Restore view state
      if (context.view) {
        console.log('[AppContent] Restoring view:', context.view);
        setView(context.view);
      }
      
      // 🔹 STEP 3: Restore modal state (with delay for component readiness)
      if (context.albumFromPastOpen) {
        console.log('[AppContent] Reopening album editor...');
        // Set flag so AlbumFromPast knows we're returning from preview
        sessionStorage.setItem('vvd:returningFromPreview', 'true');
        setTimeout(() => {
          setAlbumFromPastOpen(true);
          console.log('[AppContent] ✅ Album editor reopened');
        }, 300); // Slightly longer delay to ensure trip data is loaded
      }
      
      console.log('[AppContent] ✅ State restoration complete');
      
      // Clean up sessionStorage
      sessionStorage.removeItem('vvd:returnContext');
      sessionStorage.removeItem('vvd:currentTripId');
      
    } catch (e) {
      console.error('[AppContent] Failed to restore context:', e);
      // Clean up on error
      try {
        sessionStorage.removeItem('vvd:returnContext');
        sessionStorage.removeItem('vvd:currentTripId');
      } catch (_) {}
    }
  };
  
  // Run async restoration
  restoreState();
}, []); // Run once on mount

// 🔹 Auto-save trip when navigating away (triggered by AlbumFromPast)
useEffect(() => {
  const handleSaveTrip = async () => {
    console.log('[AppContent] Received save trip request');
    
    if (!tripName || !tripName.trim()) {
      console.log('[AppContent] No trip name, skipping auto-save');
      return;
    }
    
    try {
      // Prepare trip data (same structure as saveTripNow)
      const tripData = {
        id: currentTripId || slugifyName(tripName),
        name: tripName,
        steps: rawSteps,
        savedAt: lastSavedAt || Date.now(),
        updatedAt: Date.now()
      };
      
      console.log('[AppContent] Auto-saving trip:', tripData.name);
      
      // Save to IndexedDB
      const savedId = await saveTrip(tripData);
      
      // Update state
      setCurrentTripId(savedId);
      setLastSavedAt(tripData.updatedAt);
      
      // Store tripId in sessionStorage so context can include it
      sessionStorage.setItem('vvd:currentTripId', savedId);
      
      console.log('[AppContent] ✅ Trip auto-saved with ID:', savedId);
    } catch (error) {
      console.error('[AppContent] Failed to auto-save trip:', error);
    }
  };
  
  window.addEventListener('app:saveTrip', handleSaveTrip);
  return () => window.removeEventListener('app:saveTrip', handleSaveTrip);
}, [tripName, rawSteps, currentTripId, lastSavedAt]);

useEffect(() => {
  const onHardwareBack = (e) => {
    console.log('🎯 [AppContent] Back event, states:', {
      albumFromPastOpen,
      albumOpen,
      editing: !!editing,
      showTripList,
      showDigestDialog,
      view
    });

        // If a child (like AlbumFromPast) already handled this, do nothing here
    if (e.defaultPrevented) {
      console.log('[AppContent] Event already handled by child, skipping AppContent handler');
      return;
    }


    // 1) Close any open overlay/modal first
    if (albumFromPastOpen) { 
      console.log('[AppContent] ✅ Closing albumFromPast with full cleanup');
      // 🔹 FIX: Full cleanup using centralized helper
      hardResetCurrentTripFlow();
      setAlbumFromPastOpen(false);
      setPastTripGenerating(false);
      setPastTripGatheringPhotos([]);
      e.preventDefault(); 
      return; 
    }
    if (albumOpen) { 
      console.log('[AppContent] ✅ Closing albumWizard');
      setAlbumOpen(false); 
      e.preventDefault(); 
      return; 
    }
    if (editing) { 
      console.log('[AppContent] ✅ Closing editor');
      setEditing(null); 
      e.preventDefault(); 
      return; 
    }
    if (showTripList) { 
      console.log('[AppContent] ✅ Closing tripList');
      setShowTripList(false); 
      e.preventDefault(); 
      return; 
    }
    if (showDigestDialog) { 
      console.log('[AppContent] ✅ Closing digestDialog');
      setShowDigestDialog(false); 
      e.preventDefault(); 
      return; 
    }

    // 2) Pop internal view: album → past → start
    if (view === 'album') { 
      console.log('[AppContent] ✅ album -> past');
      setView('past'); 
      e.preventDefault(); 
      return; 
    }
    
    if (view === 'past')  {
      // Check if child components have overlays open
      if (window.__vividaOverlayOpen || window.__vividaViewAllPagesOpen) {
        console.log('[AppContent] ⚠️ Overlay open, letting child handle it');
        // Don't preventDefault - let the child component handle it
        return;
      }
      console.log('[AppContent] ✅ past -> start');
      
      setView('start');
      e.preventDefault(); 
      return; 
    }


    console.log('[AppContent] ⚠️ No action, allowing fallback');
  };
  
  window.addEventListener('app:hardwareBack', onHardwareBack);
  return () => window.removeEventListener('app:hardwareBack', onHardwareBack);
}, [albumFromPastOpen, albumOpen, editing, showTripList, showDigestDialog, view]);


    // When Current Trip (Daily Digest) kicks us into Past view on web,
  // auto-open the hidden file input once the Past view is mounted.
  useEffect(() => {
    // Only act when:
    //  - we're on the Past view
    //  - we have a pending digest after import
    //  - and we're NOT on native Android (Android uses handleNativePick)
    if (view !== 'past') return;
    if (!pendingDigestAfterImport) return;
    if (isNativeAndroid() || isNativeIOS()) return;  // Native platforms use handleNativePick

    // Defer to the next tick so the DOM is fully updated
    const id = setTimeout(() => {
      const input = document.getElementById('fileInput');
      if (input) {
        input.value = '';
        input.click();
      }
      // NOTE: do NOT clear pendingDigestAfterImport here.
      // We want onFilesSelected/handleNativePick to own that, so they
      // can also decide whether to show the dialog or not.
    }, 0);

    return () => clearTimeout(id);
  }, [view, pendingDigestAfterImport]);



  const [expandedSteps, setExpandedSteps] = useState(() => new Set());
  const [progress, setProgress] = useState({ total: 0, done: 0 });
  const [resolving, setResolving] = useState(false);

// Cancellation flag for long-running import/geocoding work
const cancelResolveRef = useRef(false);

// Auto-hide timer for ResolvingLoader overlay (does NOT stop resolving)
const loaderAutoHideTimerRef = useRef(null);

// 🔹 FIX: Track when import completed to prevent Safety Valve race condition
// On iOS/web, import finishes before geocodingInProgress useMemo can update
const importCompletedAtRef = useRef(null);

// 🔹 FIX: Track if import is actively in progress (Safety Valve must wait)
const importInProgressRef = useRef(false);

// 🔹 FIX: Counter to force Safety Valve recheck after grace period
const [safetyValveRecheckCount, setSafetyValveRecheckCount] = useState(0);






// Resolving loader state
const [showResolvingLoader, setShowResolvingLoader] = useState(false);
const [resolvingProgress, setResolvingProgress] = useState(0);
const [resolvingStatus, setResolvingStatus] = useState('');

// Track *why* the loader is visible (import | geocoding). Null means "don't show".
const [loaderContext, setLoaderContext] = useState(null);

// Briefly suppress showing the loader right after a step save to avoid UX flicker.
const [suppressLoader, setSuppressLoader] = useState(false);

// Central cancel handler for the blocking ResolvingLoader (import + geocoding + generation)
const cancelResolvingFlow = useCallback(() => {
  cancelResolveRef.current = true;

  if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER) {
    setShowResolvingLoader(false);
    setLoaderContext(null);
    setResolvingProgress(0);
    setResolvingStatus('');
  }

  // Do not mutate rawSteps here; just stop the current pipeline and return control to the user
  setProgress({ total: 0, done: 0 });
  setResolving(false);
}, [setShowResolvingLoader, setLoaderContext, setResolvingProgress, setResolvingStatus, setProgress, setResolving]);

// 🔹 NEW: Track if we're in the Current Trip digest flow
// This prevents geocoding from prematurely ending the loader
const [digestFlowActive, setDigestFlowActive] = useState(false);

const digestFlowActiveRef = useRef(false); // 🔹 ADD THIS

// 🔹 ADD THIS: Keep ref in sync with state
useEffect(() => {
  digestFlowActiveRef.current = digestFlowActive;
}, [digestFlowActive]);

// 🔹 NEW: Explicit state for the gathering animation (separate from digestFlowActive)
// This ensures the animation shows regardless of other state changes
const [showGatheringLoader, setShowGatheringLoader] = useState(false);

// 🔹 FIX: Track when we're waiting for album to mount and generate
// This keeps the gathering loader visible until onAlbumReady is called
const [awaitingAlbumReady, setAwaitingAlbumReady] = useState(false);

const [awaitingAlbumReadySource, setAwaitingAlbumReadySource] = useState(null); // "digest" | "past" | null
// 🔹 NEW: Store photos for the gathering animation
const [gatheringPhotos, setGatheringPhotos] = useState([]);

// 🔹 NEW: State for Past Trip album generation animation (separate from Current Trip)
const [pastTripGenerating, setPastTripGenerating] = useState(false);
const [pastTripGatheringPhotos, setPastTripGatheringPhotos] = useState([]);

// 🔹 FIX JITTER: Unique session ID to force AlbumGatheringLoader remount on each flow
// Incrementing this key ensures animation state is completely fresh each time
const [gatheringSessionId, setGatheringSessionId] = useState(0);

// 🔹 FIX JITTER: Session ID to prevent stale progress updates
const currentFlowSessionRef = useRef(0);

// 🔹 FIX JITTER: Tracks the deferred native picker open for Current Trip (prevents overlap/jitter)
const currentTripPickTimerRef = useRef(null);

// 🔹 FIX: Track when gathering loader became visible for minimum duration enforcement
const gatheringLoaderVisibleAtRef = useRef(null);

// 🔹 FIX: Refs to avoid stale state inside delayed callbacks (iOS/Android)
const awaitingAlbumReadySourceRef = useRef(null);
const awaitingAlbumReadyRef = useRef(false);

// Keep refs in sync with state
useEffect(() => { awaitingAlbumReadySourceRef.current = awaitingAlbumReadySource; }, [awaitingAlbumReadySource]);
useEffect(() => { awaitingAlbumReadyRef.current = awaitingAlbumReady; }, [awaitingAlbumReady]);

const clearCurrentTripPickTimer = () => {
  if (currentTripPickTimerRef.current) {
    clearTimeout(currentTripPickTimerRef.current);
    currentTripPickTimerRef.current = null;
  }
};

// One-stop cleanup for Current Trip "gathering" UI + any deferred picker
const hardResetCurrentTripFlow = () => {
  // 🔹 FIX: Increment session to invalidate stale updates
  currentFlowSessionRef.current += 1;

  clearCurrentTripPickTimer();
  setPendingDigestAfterImport(false);
  setAwaitingAlbumReady(false);
  setAwaitingAlbumReadySource(null);
  setShowGatheringLoader(false);
  setDigestFlowActive(false);
  setSuppressLoader(false);
  setGatheringPhotos([]);
  setDigestMode(null);
  gatheringLoaderVisibleAtRef.current = null; // 🔹 FIX: Clear visibility timestamp
};

  // 🔹 Derived: Gathering animation is ONLY considered "active"
  // when we're in the Current Trip digest flow OR waiting for album to be ready.
  // 🔹 FIX: Removed digestMode === 'current' check because startNewTrip() resets digestMode to null
  // before the native picker runs. digestFlowActive alone is sufficient to indicate Current Trip flow.
  const isGatheringActive =
    (showGatheringLoader && digestFlowActive) ||
    awaitingAlbumReady;  // 🔹 SIMPLIFIED: Don't require source check

    // (Removed blocking debug alert that can freeze native WebViews)
useEffect(() => {
  if (FEATURE_FLAGS.DEBUG_MODE && !isGatheringActive && awaitingAlbumReady) {
    console.warn(
      '[Debug] Loader would hide unexpectedly',
      { showGatheringLoader, digestFlowActive, awaitingAlbumReady, awaitingAlbumReadySource }
    );
  }
}, [isGatheringActive, showGatheringLoader, digestFlowActive, awaitingAlbumReady, awaitingAlbumReadySource]);

    // 🔹 NEW: Past Trip gathering - separate from Current Trip digest flow
const isPastTripGatheringActive =
    (pastTripGenerating && pastTripGatheringPhotos.length > 0 && !digestMeta) ||
    (awaitingAlbumReady && awaitingAlbumReadySource === "past");


// 🔹 DEBUG: Track gathering loader state changes
useEffect(() => {
  console.log('[DEBUG] Gathering loader state:', {
    showGatheringLoader,
    digestFlowActive,
    awaitingAlbumReady,
    isGatheringActive: (showGatheringLoader && digestFlowActive) || awaitingAlbumReady,
    photosCount: gatheringPhotos?.length || 0
  });
}, [showGatheringLoader, gatheringPhotos?.length, digestFlowActive, awaitingAlbumReady]);

// 🔹 FIX: Track when gathering loader becomes visible for minimum duration enforcement
useEffect(() => {
  const isVisible = isGatheringActive || isPastTripGatheringActive;
  if (isVisible) {
    if (!gatheringLoaderVisibleAtRef.current) {
      gatheringLoaderVisibleAtRef.current = Date.now();
      console.log('[GatheringLoader] Became visible at:', gatheringLoaderVisibleAtRef.current);
    }
  } else {
    if (gatheringLoaderVisibleAtRef.current) {
      const duration = Date.now() - gatheringLoaderVisibleAtRef.current;
      console.log('[GatheringLoader] Was visible for:', duration, 'ms');
    }
    gatheringLoaderVisibleAtRef.current = null;
  }
}, [isGatheringActive, isPastTripGatheringActive]);

// 🔹 NEW: Simulate progress for Past Trip album generation
useEffect(() => {
  if (!pastTripGenerating) return;
  
  setResolvingProgress(10);
  
  const stages = [
    { delay: 400, progress: 25 },
    { delay: 900, progress: 45 },
    { delay: 1500, progress: 65 },
    { delay: 2200, progress: 80 },
  ];
  
  const timers = stages.map(({ delay, progress }) => 
    setTimeout(() => {
      if (pastTripGenerating) setResolvingProgress(progress);
    }, delay)
  );
  
  return () => timers.forEach(clearTimeout);
}, [pastTripGenerating]);

  const [controlsCollapsed, setControlsCollapsed] = useState(false);
  const [gridVisibleCounts, setGridVisibleCounts] = useState({});
  const [theme, setTheme] = useState('dark');
  const [showHelp, setShowHelp] = useState(false);

  const countryCacheRef = useRef(new Map());
  const countryPrefetching = useRef(false);
  const cityCacheRef = useRef(new Map());
  const cityPrefetching = useRef(false);

  // ------------------------------ Persistent city-cache (1 month TTL) ------------------------------
const CITY_CACHE_STORAGE_KEY = 'vvd:cityCache:v1';
const CITY_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function safeJsonParse(str, fallback) {
  try { return JSON.parse(str); } catch { return fallback; }
}

function pruneAndLoadCityCache() {
  try {
    const raw = localStorage.getItem(CITY_CACHE_STORAGE_KEY);
    const data = safeJsonParse(raw, null);
    if (!data || typeof data !== 'object') return;

    const now = Date.now();
    const m = new Map();

    // data shape: { [key: string]: { exp: number, payload: object } }
    for (const [k, v] of Object.entries(data)) {
      if (!v || typeof v !== 'object') continue;
      const exp = Number(v.exp || 0);
      if (!exp || exp <= now) continue;
      if (v.payload && typeof v.payload === 'object') {
        m.set(k, v.payload);
      }
    }

    cityCacheRef.current = m;
  } catch {
    // ignore storage errors
  }
}

let cityCacheWriteTimer = null;
function persistCityCacheDebounced() {
  try {
    if (cityCacheWriteTimer) clearTimeout(cityCacheWriteTimer);
    cityCacheWriteTimer = setTimeout(() => {
      try {
        const now = Date.now();
        const obj = {};
        for (const [k, payload] of cityCacheRef.current.entries()) {
          obj[k] = { exp: now + CITY_CACHE_TTL_MS, payload };
        }
        localStorage.setItem(CITY_CACHE_STORAGE_KEY, JSON.stringify(obj));
      } catch {
        // ignore write errors
      }
    }, 250);
  } catch {
    // ignore timer errors
  }
}

// Load once on mount
useEffect(() => {
  pruneAndLoadCityCache();
}, []);


  // ------------------------------------------------------------
// Resolving pipeline diagnostics (single stage ref, run id)
// ------------------------------------------------------------
const resolvingRunIdRef = useRef(0);
const resolvingStageRef = useRef('idle'); // 'idle' | 'picking' | 'importing' | 'buildingSteps' | 'geocoding' | 'finalizing' | 'done' | 'canceled' | 'error'

function setResolvingStage(stage, extra = {}) {
  resolvingStageRef.current = stage;
  // Keep this log during debugging; remove later if you want.
  console.log('[ResolvingStage]', {
    runId: resolvingRunIdRef.current,
    stage,
    resolving,
    countryPrefetching: countryPrefetching.current,
    cityPrefetching: cityPrefetching.current,
    rawSteps: rawSteps?.length || 0,
    ...extra,
  });
}


  // Used to auto-scroll to the preview once an album is generated
const albumPreviewRef = useRef(null);


  // Check if geocoding is still in progress for any photos
  const geocodingInProgress = useMemo(() => {
// 🔹 FIX: Don't report geocoding in progress when album is already showing
    if (albumFromPastOpen) return false;

    if (resolving) return true; // Actively resolving now
    //commented out the below line to see if it fix the android resolving loader being stuck
    //if (countryPrefetching.current || cityPrefetching.current) return true; // Background processing
    
    // Get steps that have been manually edited (have a location field populated)
    const manuallyEditedStepKeys = new Set(
      rawSteps
        .filter(s => s.location && s.location.trim() !== '')
        .map(s => s._key || s.id)
    );
    
    // Check if there are photos with GPS but missing location data
    // SKIP photos from steps that have been manually edited
    const photosNeedingGeocode = rawSteps.flatMap(s => {
      // Skip this step's photos if the step has been manually edited
      if (manuallyEditedStepKeys.has(s._key || s.id)) return [];
      
      return (s.photos || []);
    })
      .filter(p => {
        if (!isValidCoord(p?.coordinates)) return false;
        const addr = p?.address || {};
        const hasCountry = !!(addr?.country || addr?.country_name || addr?.country_code);
        const hasCity = !!(addr?.city || addr?.town || addr?.village || addr?.municipality);
        return !hasCountry || !hasCity; // Need either country or city
      });
    
    // 🔹 DEBUG: Log geocoding check results
    const allPhotos = rawSteps.flatMap(s => s.photos || []);
    const photosWithCoords = allPhotos.filter(p => isValidCoord(p?.coordinates));
    console.log('[geocodingInProgress] Total photos:', allPhotos.length, 
      '| With coords:', photosWithCoords.length,
      '| Needing geocode:', photosNeedingGeocode.length,
      '| resolving:', resolving,
      '| Result:', photosNeedingGeocode.length > 0);
    
    return photosNeedingGeocode.length > 0;
  }, [rawSteps, resolving]);

  // ------------------------------------------------------------
  // 🔹 FIX: Safety Valve for Non-GPS Imports
  // If importing is done (loader visible) but no geocoding is needed,
  // we must manually turn off the loader, otherwise it hangs forever.
  // 
  // 🔹 UPDATED: Added grace period to prevent race condition on iOS/web
  // where this fires before geocodingInProgress can update after import
  // ------------------------------------------------------------
  useEffect(() => {
    // 🔹 DEBUG: Log every time this effect runs
    console.log('[SafetyValve] Effect running:', {
      showResolvingLoader,
      suppressLoader,
      digestFlowActive,
      resolving,
      geocodingInProgress,
      loaderContext,
      importInProgress: importInProgressRef.current,
      importCompletedAt: importCompletedAtRef.current,
      timeSinceImport: importCompletedAtRef.current ? Date.now() - importCompletedAtRef.current : null
    });
    
    // 1. Only run if the loader is currently visible and NOT suppressed
    if (!showResolvingLoader || suppressLoader) {
      console.log('[SafetyValve] Bailing: loader not visible or suppressed');
      return;
    }

    // 2. Only run if we are NOT in the special "Digest/Current Trip" flow
    // (That flow has its own separate loader logic)
    if (digestFlowActive) {
      console.log('[SafetyValve] Bailing: digest flow active');
      return;
    }

    // 3. Only run if we are NOT actively resolving (geocoding API is idle)
    if (resolving) {
      console.log('[SafetyValve] Bailing: actively resolving');
      return;
    }
    
    // 🔹 FIX: Don't close if import is still in progress
    if (importInProgressRef.current) {
      console.log('[SafetyValve] Bailing: import still in progress');
      return;
    }

    // 4. CRITICAL CHECK: If geocoding is NOT needed...
    if (!geocodingInProgress) {
      
      // ...check if we are stuck in the "import" or "picking" stage.
      // If the progress is high (meaning import finished) but context is still 'import',
      // we are stuck waiting for a handoff that won't happen.
      if (loaderContext === 'import') {
        
        // 🔹 FIX: Grace period - don't close if import just completed
        // On iOS/web, import finishes before geocodingInProgress can update
        const importCompletedAt = importCompletedAtRef.current;
        const now = Date.now();
        const gracePeriodMs = 2500; // 2.5 seconds grace period
        
        if (importCompletedAt && (now - importCompletedAt) < gracePeriodMs) {
          console.log('[SafetyValve] Import just completed, waiting for geocoding to stabilize...');
          
          // Schedule a forced recheck after grace period
          const recheckTimer = setTimeout(() => {
            console.log('[SafetyValve] Grace period expired, forcing recheck...');
            importCompletedAtRef.current = null; // Clear so next run proceeds
            setSafetyValveRecheckCount(c => c + 1); // Force effect to re-run
          }, gracePeriodMs - (now - importCompletedAt) + 100);
          
          return () => clearTimeout(recheckTimer);
        }
        
        console.log('[SafetyValve] Import finished, no GPS data found. Closing loader.');
        
        // Add a tiny delay for UX (so user sees "100%") then close
        const timer = setTimeout(() => {
          setShowResolvingLoader(false);
          setLoaderContext(null);
          setResolvingProgress(0);
          setResolvingStatus('');
        }, 500);

        return () => clearTimeout(timer);
      }
    } else {
      console.log('[SafetyValve] Geocoding still in progress, keeping loader visible');
    }
  }, [
    showResolvingLoader, 
    suppressLoader, 
    digestFlowActive, 
    resolving, 
    geocodingInProgress, 
    loaderContext,
    safetyValveRecheckCount // 🔹 FIX: Recheck when counter increments
  ]);

  // OPTIMIZED: Auto resolve city/country with new clustering approach
  const autoResolveRef = useRef(false);
  
  // Define the optimized resolver before the effect that uses it
  // --- REPLACED FUNCTION ---
  // This version fixes the Android ANR crash and iOS "loop"
  // by using proper batching for state updates.
  // It uses your *existing* 'setResolving' state to avoid the freeze.
  const resolveAllPlacesOptimized = useCallback(async () => {
// 🔹 FIX: Don't run geocoding when album is already showing
    if (albumFromPastOpen) {
      console.log('[Geocoding] Skipping - album is already open');
      return;
    }

    // 🛡️ Use existing 'resolving' state as a re-entry guard
    if (resolving) {
      console.log('Auto-resolution already in progress, skipping');
      return;
    }

    // Reset cancellation at the start of a new resolution run
    cancelResolveRef.current = false;

    const rawPhotos = rawSteps.flatMap(s => s.photos || []);
    if (rawPhotos.length === 0) {
      console.log('No photos to resolve');
      // 🔹 FIX: If in digest flow, update progress to show we're moving to album generation
      if (digestFlowActive && FEATURE_FLAGS.SHOW_RESOLVING_LOADER) {
        setResolvingProgress(85);
        setResolvingStatus('Preparing album...');
      }
      return;
    }

    // 1. REMOVED: Auto-attach live GPS was causing photos without GPS to default to user's current location
    // This was the bug causing "United States, Franklin" to appear for photos without GPS
    // Photos without GPS in EXIF should remain without location data
    
    // 2. Get a final list of all photos that already have GPS (from EXIF extraction)
    const photosWithGPS = rawSteps
  .flatMap(s => s.photos || [])
  .filter(p => (p.hasGPS || p.gpsExtracted) && isValidCoord(p.coordinates) && !(p.address?.city))
  .slice()
  .sort((a, b) => {
    const ta = new Date(a.dateTaken || a.date || 0).getTime();
    const tb = new Date(b.dateTaken || b.date || 0).getTime();
    // Put unknown dates last
    if (!Number.isFinite(ta) && !Number.isFinite(tb)) return 0;
    if (!Number.isFinite(ta)) return 1;
    if (!Number.isFinite(tb)) return -1;
    return ta - tb; // ✅ oldest first
  });


    if (photosWithGPS.length === 0) {
      console.log('No photos need geocoding.');
      // 🔹 FIX: If in digest flow, update progress to show we're moving to album generation
      if (digestFlowActive && FEATURE_FLAGS.SHOW_RESOLVING_LOADER) {
        setResolvingProgress(85);
        setResolvingStatus('Preparing album...');
      }
      return;
    }

    console.log(`Starting auto city resolution: ${photosWithGPS.length} photos need city names.`);
    
    // 🛡️ Set existing 'resolving' state
    setResolving(true);

    // Continue loader from import phase (start at 40%)
    // 🔹 FIX: Skip ResolvingLoader during digest flow - GatheringLoader handles it
    if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER && !digestFlowActive && !suppressLoader) {
      
      if (!showResolvingLoader) {
        setShowResolvingLoader(true);
      }
      setResolvingProgress(40);
      setResolvingStatus('Starting location resolution...');
    } else if (digestFlowActive) {
      // During digest flow, just log progress without showing ResolvingLoader
      console.log('[Geocoding] Digest flow active - keeping GatheringLoader, skipping ResolvingLoader');
    }
    
    // Auto-hide loader after 3 minutes (resolving continues; UI remains disabled via `resolving`)
if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER) {
  if (loaderAutoHideTimerRef.current) clearTimeout(loaderAutoHideTimerRef.current);

  loaderAutoHideTimerRef.current = setTimeout(() => {
    // Do NOT change `resolving`; only stop showing the overlay.
    console.log('[ResolvingLoader] Auto-hiding after 3 minutes (background resolving continues)');
    setShowResolvingLoader(false);
    setLoaderContext(null);

    // Optional: leave progress/status as-is, or clear them:
    // setResolvingProgress(0);
    // setResolvingStatus('');
  }, 180000);
}


    try {
      // 3. Build clusters to reduce API calls
      const clusters = buildClusters(photosWithGPS, 0.01); // i changed this from 0.5 to 0.01 this will give more accuracy to location. but if delayed geo decoding, we may need to change it back
      const clusterArray = Array.from(clusters.entries());
      const totalClusters = clusterArray.length;
      
      console.log(`Built ${totalClusters} clusters from ${photosWithGPS.length} photos`);

      let resolved = 0;
      
      // 4. Set adaptive limits based on import size
      const BATCH_SIZE = photosWithGPS.length > 400 ? 2 :
                         photosWithGPS.length > 200 ? 3 :
                         photosWithGPS.length > 100 ? 5 : 8;
      
      // 🔹 CHANGE 3: iOS concurrency limit - cap at 2 to prevent overwhelming the device
      const isiOS = (typeof isNativeIOS === 'function' && isNativeIOS()) || 
                    /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      const CONCURRENCY_BASE = photosWithGPS.length > 300 ? 3 :
                               photosWithGPS.length > 150 ? 5 : 8;
      
      const CONCURRENCY = isiOS ? Math.min(2, CONCURRENCY_BASE) : CONCURRENCY_BASE;
      
      const API_DELAY = photosWithGPS.length > 300 ? 200 : 150;
      
      console.log(`Geocoding config: batch=${BATCH_SIZE}, concurrency=${CONCURRENCY}, delay=${API_DELAY}ms`);
      
      // 5. This map accumulates updates instead of setting state in the loop
      const clusterUpdates = new Map();

      await poolMap(
        clusterArray,
        async ([clusterKey, cluster], index) => {
          if (cancelResolveRef.current) return;

          let payload = cityCacheRef.current.get(clusterKey);
          
          if (!payload) {
            const { lat, lng } = cluster.representative;
            try {
              payload = await reverseGeocode(lat, lng);
              if (payload) {
                cityCacheRef.current.set(clusterKey, payload);
              }
            } catch (error) {
              console.warn(`Failed to geocode cluster ${index}:`, error);
              payload = null;
            }
            await new Promise(r => setTimeout(r, API_DELAY));
          }

          // 6. Accumulate updates instead of calling setRawSteps
          if (payload && (payload.city || payload.town || payload.village)) {
            clusterUpdates.set(clusterKey, { cluster, payload });
          }

          resolved++;

          // 7. 🚀 BATCHING LOGIC: Apply updates in chunks
          // 🔹 FIX: If "Flying Photos" (digestFlowActive) is running, buffer ALL updates until the end.
          // This prevents rapid state updates from stuttering the animation.
          const isFinalBatch = resolved === totalClusters;
          const shouldUpdateUI = digestFlowActive 
            ? isFinalBatch 
            : (resolved % BATCH_SIZE === 0 || isFinalBatch);

          if (shouldUpdateUI) {
            console.log(`Batch checkpoint: ${resolved}/${totalClusters} clusters (${clusterUpdates.size} pending updates)`);
            
            // Update loader progress (40-100% for geocoding)
            // Update loader progress (40-100% for geocoding)
            if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER) {
              const geocodingProgress = 40 + Math.round((resolved / totalClusters) * 60);
              setResolvingProgress(geocodingProgress);
              setResolvingStatus(`Resolving locations... ${resolved}/${totalClusters}`);
            }
            
            // 🔹 CHANGE 4: Snapshot AND clear immediately (race condition fix)
            // We must clear the map BEFORE awaiting, so that any new results 
            // coming in from other workers during the await are NOT wiped out.
            const updateArray = Array.from(clusterUpdates.entries());
            clusterUpdates.clear();
            
            // Apply updates in small sub-batches
            const SUB_BATCH_SIZE = photosWithGPS.length > 400 ? 30 :
                                   photosWithGPS.length > 200 ? 50 : 75;
            
            for (let i = 0; i < updateArray.length; i += SUB_BATCH_SIZE) {
              const subBatch = updateArray.slice(i, i + SUB_BATCH_SIZE);
              
              await new Promise(resolve => {
                setRawSteps(prev => {
                  const next = [...prev];
                  
                  for (const [key, { cluster, payload }] of subBatch) {
                    for (const photo of cluster.photos) {
                      // Find this photo in the state and update it
                      for (let stepIdx = 0; stepIdx < next.length; stepIdx++) {
                        const step = next[stepIdx];
                        const photoIdx = step.photos.findIndex(p => p.url === photo.url);
                        
                        if (photoIdx !== -1) {
                          const updatedPhoto = {
                            ...step.photos[photoIdx],
                            address: { // Merge address data
                              ...(step.photos[photoIdx].address || {}),
                              ...payload,
                              city: payload.city || payload.town || payload.village || payload.municipality,
                              country: (step.photos[photoIdx].address?.country) || payload.country,
                            },
                            gpsExtracted: true
                          };
                          
                          // Create a new step with the updated photo
                          const newPhotos = [...step.photos];
                          newPhotos[photoIdx] = updatedPhoto;
                          next[stepIdx] = { ...step, photos: newPhotos };

                          // Also update step-level address if it's missing
                          if (!next[stepIdx].address?.city) {
                            next[stepIdx].address = updatedPhoto.address;
                          }
                          break; // Found photo, move to next in cluster
                        }
                      }
                    }
                  }
                  return next;
                });
                
                // 8. 🚀 Pause to let the main thread "breathe"
                setTimeout(resolve, 20);
              });
            }
            // clusterUpdates already cleared above before the async work
          }
        },
        CONCURRENCY
      );

      console.log(`Auto city resolution complete: ${resolved}/${totalClusters} clusters processed`);
      
      // Complete geocoding phase
      if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER) {
        // 🔹 FIX: If in digest flow, don't signal completion yet
        // The album generation will handle the final completion
        if (digestFlowActive) {
  // 🔹 FIX: Only set if we're still in an active digest flow
  // The progress will be set by handleGenerateDailyDigest instead
  // setResolvingProgress(85); // REMOVED - causes flickering with album generation progress
  console.log('[Geocoding] Digest flow active, skipping progress update (album handles it)');
          setResolvingStatus('Preparing album...');
          // DON'T trigger onComplete - let album generation finish
          console.log('[Geocoding] Digest flow active, holding loader for album generation');
        } else {
  // 🔹 FIX: Only update if not in digest flow
  if (!digestFlowActive) {
    setResolvingProgress(100);
    setResolvingStatus('All locations resolved!');
  }
        }
      }

    } catch (error) {
      console.error('Auto city resolution failed:', error);
      
      // Hide loader on error - BUT NOT during digest flow (let album generation continue)
      if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER && !digestFlowActive) {
        setShowResolvingLoader(false);
      }
    } finally {
      // 🛡️ Clear existing 'resolving' state
      setResolving(false);
      console.log('Geocoding complete, resolving flag set to false.');
    }
  }, [rawSteps, resolving, reverseGeocode, poolMap, maybeAttachLiveGPS, buildClusters, digestFlowActive]);

  useEffect(() => {
// 🔹 FIX: Don't start new geocoding when album is already open
    if (albumFromPastOpen) return;

    if (autoResolveRef.current) return;
    if (resolving) return;
    if (!geocodingInProgress) return; // Use geocodingInProgress instead of resolveEligibleCount

    autoResolveRef.current = true;
    (async () => {
      try { await resolveAllPlacesOptimized(); }
      finally { autoResolveRef.current = false; }
    })();
  }, [geocodingInProgress, resolving, resolveAllPlacesOptimized]);

  const steps = useMemo(() => {
    let base = rawSteps
      .map(s => ({ ...s, photos: (s.photos || []).map(p => ({ ...p, likes: p.likes || 0 })) }))
      .sort((a, b) => {
        const d = (a.date || '').localeCompare(b.date || '');
        if (d !== 0) return d;
        const an = (stepHeaderPlace(a) || '').toLowerCase();
        const bn = (stepHeaderPlace(b) || '').toLowerCase();
        return an.localeCompare(bn);
      });

    if (dateFilterEnabled) {
      if (fromDate) base = base.filter(s => (s.date || '') >= fromDate);
      if (toDate)   base = base.filter(s => (s.date || '') <= toDate);
    }

    base = applySelection(base, { limitEnabled, limitCount, strategy: selectionStrategy });
    return base;
  }, [rawSteps, fromDate, toDate, dateFilterEnabled, limitEnabled, limitCount, selectionStrategy]);

    const stepsForAlbum = useMemo(() => {
    // Helper: get earliest photo timestamp from a step (for time-of-day sorting)
    const getEarliestPhotoTime = (step) => {
      const photos = step.photos || [];
      let earliest = null;
      for (const p of photos) {
        const dt = p.dateTaken || p.date;
        if (dt) {
          const time = new Date(dt).getTime();
          if (!isNaN(time) && (earliest === null || time < earliest)) {
            earliest = time;
          }
        }
      }
      return earliest;
    };

    // For album generation, make sure each step has a human-friendly location string
    // AND is sorted chronologically (oldest to newest)
    return (rawSteps || [])
      .map((step) => {
        const existing = (step.location || '').trim();
        if (existing) return step;  // keep any user-edited locations as-is

        const auto = formatOneLineLocation(step);
        if (!auto) return step;     // no address info → leave it alone (may show "Unknown location")

        // Provide a location field just for the album templates
        return {
          ...step,
          location: auto,
        };
      })
      // Sort chronologically: by date first, then by earliest photo time within that day
      .sort((a, b) => {
        // Primary: sort by date (YYYY-MM-DD string comparison)
        const dateCompare = (a.date || '').localeCompare(b.date || '');
        if (dateCompare !== 0) return dateCompare;
        
        // Secondary: sort by earliest photo time (for same-day chapters)
        const aTime = getEarliestPhotoTime(a);
        const bTime = getEarliestPhotoTime(b);
        if (aTime !== null && bTime !== null) return aTime - bTime;
        if (aTime !== null) return -1;  // a has time, b doesn't → a first
        if (bTime !== null) return 1;   // b has time, a doesn't → b first
        return 0;  // neither has time → keep original order
      });
  }, [rawSteps]);


  const scrollToStep = (i) => {
    const el = document.getElementById(`step-${i}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  

  async function onFilesSelected(files) {
    if (!files || !files.length) return;

    const existing = new Set();
    rawSteps.forEach(rs => (rs.photos || []).forEach(p => {
      const of = p.originalFile;
      if (of && of.name && of.size && of.lastModified) {
        existing.add(`${of.name}::${of.size}::${of.lastModified}`);
      } else if (p.url) {
        existing.add(p.url);
      }
    }));

        // 🔹 FIX: Use digestFlowActive instead of digestMode === 'current'
        // because startNewTrip() resets digestMode to null before file picker opens
        const isCurrentTripDigestFlow = digestFlowActive || (pendingDigestAfterImport && digestMode === 'current');

    // Show loader for import phase
    if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER) {
      if (isCurrentTripDigestFlow) {
        // 👉 Current Trip digest on WEB: use ONLY the cinematic AlbumGatheringLoader
        setLoaderContext('import');

        // Block the ResolvingLoader spinner completely
        setSuppressLoader(true);
        setShowResolvingLoader(false);

        // Ensure digest flow + gathering loader are active
        setDigestFlowActive(true);
        setShowGatheringLoader(true);
        
        // 🔹 WEB FIX: Generate quick preview URLs IMMEDIATELY for all image files
        // This makes photos start flying right away, even before heavy processing begins
        // (Android gets photos with ready URLs from native bridge, web needs this)
        const quickPreviews = [];
        for (const f of files) {
      // Skip videos / unsupported files early (prevents loader from hanging)
      if (!isSupportedImageFile(f)) {
        done += 1;
        setProgress({ total: files.length, done });
        continue;
      }

      if (cancelResolveRef.current) break;
          // Skip non-images
          const isImage = f.type?.startsWith('image/') || 
                          /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(f.name);
          if (!isImage) continue;
          
          // For HEIC/HEIF, browsers can't display directly - we'll get these later
          // after conversion. For now, only show previewable formats.
          const isHeic = /\.(heic|heif)$/i.test(f.name) || 
                         f.type === 'image/heic' || 
                         f.type === 'image/heif';
          
          if (!isHeic) {
            // Create instant preview URL for regular images
            try {
              const previewUrl = URL.createObjectURL(f);
              quickPreviews.push({
                url: previewUrl,
                src: previewUrl,
                fileName: f.name,
                _isQuickPreview: true, // marker to clean up later if needed
              });
            } catch (e) {
              console.warn('[Web Preview] Could not create preview for', f.name, e);
            }
          }
        }
        
        // Feed previews immediately so flying photos start right away!
        if (quickPreviews.length > 0) {
          console.log(`[Web Preview] Feeding ${quickPreviews.length} instant previews to gathering loader`);
          setGatheringPhotos(quickPreviews.slice(0, 80)); // Cap at 80 for performance
        }
      } else {
        // 👉 All other flows (Past Trip, manual uploads): keep the ResolvingLoader behavior
        setSuppressLoader(false);
        setLoaderContext('import');
        setShowResolvingLoader(true);
        setResolvingProgress(0);
        setResolvingStatus('Preparing photos...');
        
        // 🔹 FIX: Mark import as in progress - Safety Valve must wait
        importInProgressRef.current = true;

        // ✅ Let React commit + the browser paint the loader before heavy work starts
await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      }
    }


    setProgress({ total: files.length, done: 0 });
    let done = 0;
    let next = [...rawSteps];
    
    // 🔹 FIX JITTER: Collect photos for batched updates instead of one-by-one
    let pendingGatheringPhotos = [];

    for (const f of files) {
      const sig = `${f.name}::${f.size}::${f.lastModified}`;
      if (existing.has(sig)) { done += 1; setProgress({ total: files.length, done }); continue; }
      existing.add(sig);

            try {
        const photo = await buildPhotoObject(f);
        photo.likes = photo.likes || 0;
        next = addPhotoIntoSteps(next, photo);

        // 🔹 HEIC SUPPLEMENT: For HEIC files that couldn't get quick previews,
        // add them to the animation as they're converted. Non-HEIC photos
        // already have quick previews, so this mainly helps HEIC photos.
        // 🔹 FIX: Only check digestFlowActive (not digestMode) because startNewTrip() resets digestMode
        // 🔹 FIX JITTER: Batch photo updates instead of one-by-one to reduce re-renders
        if (digestFlowActive) {
          // Collect photos in a local array and batch update every 10 photos
          // This dramatically reduces re-renders (from N to N/10)
          if (!pendingGatheringPhotos) {
            pendingGatheringPhotos = [];
          }
          pendingGatheringPhotos.push(photo);
          
          // Batch update every 10 photos to show progress without causing jitter
          if (pendingGatheringPhotos.length % 10 === 0) {
            const batchToSet = pendingGatheringPhotos.slice(0, 80);
            setGatheringPhotos(batchToSet);
          }
        }
      } catch (err) {
        console.error('Failed to process', f?.name, err);
        alert(`Could not process ${f?.name || 'a file'}.`);
      } finally {
        done += 1;
        setProgress({ total: files.length, done });
        
        // Update loader progress (0-40% for import)
        if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER) {
          const importProgress = Math.round((done / files.length) * 40);
          setResolvingProgress(importProgress);
          setResolvingStatus(`Importing photos... ${done}/${files.length}`);
        }
      }

    }

    // 🔹 FIX JITTER: Final batch update for any remaining photos
    if (digestFlowActive && pendingGatheringPhotos.length > 0) {
      setGatheringPhotos(pendingGatheringPhotos.slice(0, 80));
    }

    setRawSteps(next);
    
    // 🔹 FIX: Mark import as just completed - gives geocodingInProgress time to update
    importCompletedAtRef.current = Date.now();
    importInProgressRef.current = false; // Import finished

    // 🔹 NEW: Auto-save immediately after import
    performAutoSave(next);

if (pendingDigestAfterImport) {
  setPendingDigestAfterImport(false);

  // 🔹 FIX: Use digestFlowActive instead of digestMode === 'current'
  // because startNewTrip() resets digestMode to null before file picker opens
  if (digestFlowActive && pendingDigestConfig) {
    // Turn on digest flow + gathering loader now that import is done
    setDigestFlowActive(true);
    setShowGatheringLoader(true);

    // 🔹 UPDATED: Replace quick previews with fully processed photos
    // First, revoke any quick preview object URLs to prevent memory leaks
    setGatheringPhotos(prevPhotos => {
      if (Array.isArray(prevPhotos)) {
        prevPhotos.forEach(p => {
          if (p._isQuickPreview && p.url) {
            try { URL.revokeObjectURL(p.url); } catch {}
          }
        });
      }
      return []; // Clear before setting new
    });
    
    // Now set the fully processed photos
    const allPhotos = next.flatMap(s => s.photos || []);
    setGatheringPhotos(allPhotos);
    
    // Config-first Current Trip flow:
    // run digest immediately on the newly imported steps
    handleGenerateDailyDigest({
      ...pendingDigestConfig,
      stepsOverride: next,
    });

    setPendingDigestConfig(null);
    // NOTE: Do NOT reset digestMode here - it's needed for isGatheringActive
    // digestMode will be reset when onAlbumReady completes or user cancels
  } else {
    // Fallback for any other usage
    setShowDigestDialog(true);
  }
}


    setTimeout(() => setProgress({ total: 0, done: 0 }), 600);
  }

  

  function likePhotoByKey(stepKey, photoObj) {
  const sig = photoSignature(photoObj);
  setRawSteps(prev => {
    const next = [...prev];
    const idx = next.findIndex(s => (s._key || s.id) === stepKey);
    if (idx === -1) return prev;

    const s = { ...next[idx] };
    const arr = [...(s.photos || [])];
    const pi = arr.findIndex(pp => photoSignature(pp) === sig);
    if (pi === -1) return prev;

    const p = { ...(arr[pi] || {}) };
    const liked = (p.likes || 0) > 0;
    p.likes = liked ? 0 : 1;   // toggle 1 ↔ 0
    p.deleted = false;         // if liked, ensure not deleted
    arr[pi] = p;

    s.photos = arr;
    next[idx] = s;
    return next;
  });
}

  function deletePhotoByKey(stepKey, photoObj) {
  const sig = photoSignature(photoObj);
  setRawSteps(prev => {
    const next = [...prev];
    const idx = next.findIndex(s => (s._key || s.id) === stepKey);
    if (idx === -1) return prev;

    const s = { ...next[idx] };
    const arr = [...(s.photos || [])];
    const pi = arr.findIndex(pp => photoSignature(pp) === sig);
    if (pi === -1) return prev;

    const p = { ...(arr[pi] || {}) };
    // Toggle deletion
    p.deleted = !p.deleted;
    if (p.deleted) p.likes = 0; // deleted → unliked
    arr[pi] = p;

    s.photos = arr;
    next[idx] = s;
    return next;
  });
}

// 🔹 NEW: Helper to auto-save immediately after import
// This receives the 'fresh' steps directly, avoiding any stale state bugs.
const performAutoSave = async (stepsToSave, albumData = null) => {
  try {
    // 1. Determine name (use current name, or default to date if empty)
    let nameToUse = tripName;
    if (!nameToUse || !nameToUse.trim()) {
      const dateStr = new Date().toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      });
      nameToUse = `Trip ${dateStr}`;
      setTripName(nameToUse); // Update UI to show the assigned name
    }

    // 2. Generate ID (reuse current if exists, or make unique one)
    // We add a timestamp to the slug to prevent collisions if multiple "Trip Nov 21"s are created
    const idToUse =
      currentTripId || (slugifyName(nameToUse) + '-' + Date.now().toString(36));

    // 2.5 🔹 Preserve existing albumData if this auto-save is "steps only"
    let finalAlbumData = albumData;

    if (!finalAlbumData) {
      // Prefer the in-memory album for this trip
      if (loadedAlbumData && loadedAlbumData.pages && loadedAlbumData.pages.length) {
        finalAlbumData = loadedAlbumData;
      } else if (currentTripId) {
        // Fallback: read any existing albumData from storage so we do not wipe it
        try {
          const existing = await getTrip(idToUse);
          if (existing && existing.albumData) {
            finalAlbumData = existing.albumData;
          }
        } catch (err) {
          console.warn(
            '[AutoSave] Could not read existing trip to preserve albumData:',
            err,
          );
        }
      }
    }

    const tripData = {
      id: idToUse,
      name: nameToUse,
      steps: stepsToSave,
      savedAt: Date.now(),
      updatedAt: Date.now(),
      ...(finalAlbumData && { albumData: finalAlbumData }),
    };

    // 3. Save to storage
    await saveTrip(tripData);
    console.log(
      '[AutoSave] Saved as:',
      nameToUse,
      finalAlbumData ? '(with album)' : '',
    );

    // 4. Update state so the app knows it's clean
    setCurrentTripId(idToUse);
    setLastSavedAt(tripData.updatedAt);
    setIsAutoSaved(true);
    setAutoSavedId(idToUse);

    return idToUse; // 👈 ADD THIS RETURN STATEMENT

    // We do not lock the name here so the user can still easily rename it
  } catch (e) {
    console.warn('[AutoSave] Failed:', e);
    return currentTripId; // 👈 Fallback return
  }
};

// 🔹 Listen for album auto-save events coming from AlbumFromPast
useEffect(() => {
  function handleAutoSaveAlbum(event) {
    try {
      const albumData = event.detail && event.detail.albumData;
      if (!albumData) return;

      // 1. Save to Database (Existing logic)
      performAutoSave(rawStepsRef.current, albumData);
      
      // 2. 🔹 NEW: Update local state immediately. 
      // This ensures if you close and re-open the overlay without reloading the page, 
      // you see the latest edits, not the old initial load.
      setLoadedAlbumData(albumData); 

    } catch (e) {
      console.warn('[AutoSave] album event failed:', e);
    }
  }

  window.addEventListener('app:autoSaveAlbum', handleAutoSaveAlbum);
  return () => {
    window.removeEventListener('app:autoSaveAlbum', handleAutoSaveAlbum);
  };
}, [performAutoSave]);


  // Save current trip to localStorage (with JSON download fallback)
  const saveTripNow = useCallback(async () => {
  if (!tripName || !tripName.trim()) {
    alert('Please enter a trip name before saving.');
    return;
  }

  try {
    // Check IndexedDB support
    if (!isIndexedDBSupported()) {
      alert('Your browser does not support offline storage. Please use a modern browser.');
      return;
    }

    // Prepare trip data
    const tripData = {
      id: currentTripId || slugifyName(tripName),
      name: tripName,
      steps: rawSteps,
      savedAt: lastSavedAt || Date.now(),
      updatedAt: Date.now()
    };

    // Save to IndexedDB
    const savedId = await saveTrip(tripData);
    
    // Update component state
    setCurrentTripId(savedId);
    setIsNameLocked(true);
    setLastSavedAt(tripData.updatedAt);

    console.log('Trip saved successfully:', savedId);

    // Show success toast
    try {
      const el = document.createElement('div');
      el.textContent = `✓ Saved "${tripName}"`;
      el.style.cssText = 'position:fixed;bottom:16px;right:16px;background:#16a34a;color:white;padding:8px 12px;border-radius:8px;z-index:9999;font-weight:500;box-shadow:0 4px 6px rgba(0,0,0,0.2)';
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 2000);
    } catch {}

  } catch (error) {
    console.error('Failed to save trip:', error);
    alert('Failed to save trip. Please try again.');
  }
}, [tripName, rawSteps, currentTripId, lastSavedAt]);

// ADD this new function:
// In src/App.js

const loadTripFromStorage = useCallback(async (trip) => {
  try {
    // 1. Load Trip Data
    setTripName(trip.name || '');
    setRawSteps(trip.steps || []);
    setCurrentTripId(trip.id);
    setLastSavedAt(trip.savedAt);
    setIsNameLocked(true);
    
    // 2. Reset Auto-save Tracking
    setIsAutoSaved(false);
    setAutoSavedId(null);

    // 3. 🔹 FIX: Clear any lingering "Digest" or "Generator" state
    // This prevents the app from thinking we are in the middle of creating a different album
    setDigestMeta(null);
    setDigestMode(null);
    setPendingDigestConfig(null);
    setPendingDigestAfterImport(false);

    // 4. 🔹 FIX: Clear the session cache so AlbumFromPast starts fresh
    sessionStorage.removeItem('vvd:lastAlbumPages'); 

    // 5. Handle Album Data
    // If this specific trip has a saved album, load it into state. 
    // If NOT, make sure we clear the state so the PREVIOUS album doesn't persist.
    if (trip.albumData) {
      setLoadedAlbumData(trip.albumData); // Load THIS trip's album
      // Also update legacy localStorage just in case
      try { localStorage.setItem('tt:album:fromPast', JSON.stringify(trip.albumData)); } catch(e){}
    } else {
      setLoadedAlbumData(null); // Clear PREVIOUS trip's album
    }
    
    // 6. Switch view
    setView('past');
    setShowTripList(false);

    // Show success toast
    const el = document.createElement('div');
    el.textContent = `✓ Loaded "${trip.name}"${trip.albumData ? ' (with album)' : ''}`;
    el.style.cssText = 'position:fixed;bottom:16px;right:16px;background:#0ea5e9;color:white;padding:8px 12px;border-radius:8px;z-index:9999;font-weight:500;box-shadow:0 4px 6px rgba(0,0,0,0.2)';
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 2000);
  } catch (error) {
    console.error('Failed to load trip:', error);
    alert('Failed to load trip. Please try again.');
  }
}, []);

// ADD this new function:
const startNewTrip = useCallback(() => {
    // 1. Clear Basic Trip Data
    setTripName('');
    setRawSteps([]);
    setCurrentTripId(null);
    setLastSavedAt(null);
    setIsNameLocked(false);
    
    // 2. Clear Autosave Tracking
    setIsAutoSaved(false);
    setAutoSavedId(null);

    // 3. 🔹 Clear Album & Digest State 
    setLoadedAlbumData(null); 
    setDigestMeta(null);
    // NOTE: Do NOT reset digestMode here - startNewTrip() is called during Current Trip flow
    // and digestMode is needed. Instead, digestMode is reset in the dialog's onClose handler.
    setAlbumFromPastOpen(false); // Close any open album overlay
    
    // 🔹 FIX JITTER: Ensure we don't show old photos if the loader pops up again
    setGatheringPhotos([]);
    
    // 4. 🔹 CRITICAL FIX: Reset ONLY the ResolvingLoader states
    // This prevents the "Stuck Loader" on Android when creating a second trip
    setShowResolvingLoader(false);
    setResolving(false);
    setLoaderContext(null);
    setResolvingProgress(0);
    setResolvingStatus('');
    // 🔹 NOTE: Do NOT reset digestFlowActive, showGatheringLoader, or suppressLoader here!
    // The Current Trip flow sets them BEFORE calling startNewTrip(),
    // and resetting them here would undo those settings.
    // These flags are reset explicitly in:
    //   - onAlbumReady callback
    //   - onCancel button handler  
    //   - Safety timeout
    //   - Native picker cancel/error handlers

    // 5. 🔹 Clear Caches
    sessionStorage.removeItem('vvd:lastAlbumPages'); 
    localStorage.removeItem('tt:album:fromPast');

    // 6. Switch View
    setView('past');
    setShowTripList(false);
  }, []);



  function handleGenerateDailyDigest({ selectedDates, journalText, stepsOverride, tripTitle }) {
    // 🔹 FIX DOUBLE LOADER FLASH: Only increment session ID if loader isn't already active
    // IMPORTANT: Use REFS not state - refs are always current, state may be stale in callbacks
    const isLoaderCurrentlyActive = digestFlowActiveRef.current || awaitingAlbumReadyRef.current;
    if (!isLoaderCurrentlyActive) {
      setGatheringSessionId(prev => prev + 1); 
    } 

    const stepsSource = stepsOverride || rawSteps;
    
    if (!stepsSource || !stepsSource.length) {
      alert('You need to have a trip loaded before creating a Daily Digest.');
      return;
    }

    const dates = Array.isArray(selectedDates)
      ? selectedDates.filter(Boolean)
      : [];

    if (!dates.length) {
      alert('Please select at least one day for your Daily Digest.');
      return;
    }

    // Sort YYYY-MM-DD strings
    const sortedDates = [...dates].sort();
    const startISO = sortedDates[0];
    const endISO = sortedDates[sortedDates.length - 1];
    const selectedSet = new Set(sortedDates);

    // Only keep steps whose date is one of the selected days
    const stepsInRange = stepsSource.filter((step) => {
      const raw = step.date || step.dateTaken || step.day;
      if (!raw) return false;
      const d = String(raw).slice(0, 10);
      return selectedSet.has(d);
    });

    const photosCount = stepsInRange.reduce(
      (acc, s) => acc + ((s.photos || []).length),
      0
    );

    if (!stepsInRange.length || photosCount < 5) {
      alert(
        'Not enough photos on the selected day(s) to build a Daily Digest. Try selecting more days or import more photos.'
      );
      return;
    }

    const baseName =
      tripTitle && tripTitle.trim().length > 0
        ? tripTitle.trim()
        : 'Current Trip';

    const digestTitle = baseName;

    // From this point on, rawSteps only contains the selected days
    setRawSteps(stepsInRange);

    // Keep days count for backwards compatibility, and also store selectedDates & journal
    const cleanJournal = (journalText || '').trim();

    setDigestMeta({
      title: digestTitle,
      days: sortedDates.length,
      selectedDates: sortedDates,
      journalText: cleanJournal,
      journalHtml: cleanJournal,
      hasJournal: !!cleanJournal,
      startISO,
      endISO,
      timestamp: Date.now(),
    });

    // 🔹 Transition loader to "generating" phase
    if (FEATURE_FLAGS.SHOW_RESOLVING_LOADER && showResolvingLoader) {
      setLoaderContext('generating');
      setResolvingProgress(95);
      setResolvingStatus('Generating album...');
    }

    // 🔹 FIX: Use flushSync to force immediate state updates
    // This ensures isGatheringActive becomes true BEFORE the setTimeout fires
    flushSync(() => {
      setDigestFlowActive(true);
      setShowGatheringLoader(true);
      setAwaitingAlbumReady(true);
      setAwaitingAlbumReadySource("digest");
    });

    setShowDigestDialog(false);

    // 🔹 FIX JITTER: Delay mounting the heavy album editor by 600ms.
    setTimeout(() => {
       setView('past');
       setAlbumFromPastOpen(true);
    }, 600);
  }






  // Prepend a special empty step as "Home"
  function addHomeStep() {
    setRawSteps(prev => {
      if (hasHomeStep(prev)) return prev;
      const home = {
        _key: '_home', id: '_home', isHome: true,
        date: '', title: 'Home', location: '',
        description: '', address: null, photos: []
      };
      return [home, ...prev];
    });
  }
  function removeHomeStep() {
    setRawSteps(prev => prev.filter(s => !s.isHome));
  }

  async function updateStep(stepKey, draft) { // Make async
    let newCoords = null;
    let newAddress = null;

    // Find the *current* step data before state update
    const cur = rawSteps.find(s => (s._key || s.id) === stepKey);
    if (!cur) return;

    const oldLocation = (cur.location || '').trim();
    const newLocation = (draft.location || '').trim();

    // Check if the location string was meaningfully changed
    if (newLocation && newLocation !== oldLocation) {
      console.log(`Location changed from "${oldLocation}" to "${newLocation}". Geocoding...`);
      try {
        const geoResult = await geocode(newLocation); // Call the new geocode function
        if (geoResult && geoResult.lat && geoResult.lon) {
          newCoords = { lat: parseFloat(geoResult.lat), lng: parseFloat(geoResult.lon) };
          // Use the structured address from the geocoder if available
          newAddress = geoResult.address ? {
            ...geoResult.address,
            // Ensure city/country fields are populated from Nominatim's various properties
            city: geoResult.address.city || geoResult.address.town || geoResult.address.village || geoResult.address.municipality,
            country: geoResult.address.country || geoResult.address.country_name,
          } : null;
          console.log('Geocoding success:', newCoords, newAddress);
        } else {
          console.warn('Geocoding returned no results for:', newLocation);
        }
      } catch (e) {
        console.error('Error during geocoding:', e);
      }
    } else if (!newLocation && oldLocation) {
      console.log('Location cleared.');
      // User cleared the location, so we should clear the coordinates too
      newCoords = { lat: null, lng: null }; 
      newAddress = null;
    }

    setRawSteps(prev => {
      const next = [...prev];
      const idx = next.findIndex(s => (s._key || s.id) === stepKey);
      if (idx === -1) return prev; // Should be found, but safety check
      const curStep = next[idx];

      const updatedStep = {
        ...curStep,
        date: draft.date || curStep.date,
        title: draft.title ?? curStep.title,
        location: (draft.location ?? curStep.location) || '', // Save the user's string
        description: draft.description ?? curStep.description,
        photos: (draft.photos || []).map(p => ({ ...p })),
      };

      // If we got new coordinates, add them directly to the step
      if (newCoords) {
        updatedStep.lat = newCoords.lat; // This is what makeStepMapSVG looks for!
        updatedStep.lng = newCoords.lng; // This is what makeStepMapSVG looks for!
        
        // If we got a new address, overwrite the old one
        if (newAddress) {
          updatedStep.address = newAddress;
        } else if (newLocation === '') {
          // If user cleared location, clear address too
          updatedStep.address = null;
        }
      }

      next[idx] = updatedStep;
      // Keep steps even if they have no photos (user might be editing location first)
      // We filter empty steps on *save* not on *edit*
      return next;
    });
    setEditing(null);
  }

  // OPTIMIZED: Manual resolve function with clustering (for testing)
  const resolveAllPlacesOptimizedManual = useCallback(async () => {
    if (resolving) return;
    
    // Find photos that need city resolution
    const clusters = clusterPhotosByLocation(rawSteps);
    if (clusters.size === 0) {
      alert('No GPS-tagged photos need city resolution.');
      return;
    }

    if (!window.confirm(`Resolve cities for ${clusters.size} locations? (Was ${rawSteps.flatMap(s => s.photos || []).filter(p => isValidCoord(p?.coordinates) && !(p.address?.city)).length} individual requests, now ${clusters.size} requests)`)) {
      return;
    }

    setResolving(true);
    console.log(`Starting optimized city resolution: ${clusters.size} clusters`);
    
    try {
      const clusterArray = Array.from(clusters.entries());
      let resolved = 0;

      await poolMap(
        clusterArray,
        async ([clusterKey, cluster], index) => {
          let payload = cityCacheRef.current.get(clusterKey);
          
          if (!payload) {
            // Use representative coordinates for the API call
            const { lat, lng } = cluster.representative;
            payload = await reverseGeocode(lat, lng);
            cityCacheRef.current.set(clusterKey, payload);
            
            // Rate limit: 200ms between requests
            await new Promise(r => setTimeout(r, 200));
          }

          if (payload && (payload.city || payload.town || payload.village)) {
            // Apply to all photos in this cluster
            setRawSteps(prev => {
              const next = [...prev];
              
              cluster.items.forEach(({ stepIdx, photoIdx }) => {
                const step = next[stepIdx];
                if (!step) return;

                const photos = [...(step.photos || [])];
                const photo = photos[photoIdx];
                if (!photo) return;

                // Update photo address
                const photoAddr = { ...(photo.address || {}) };
                if (!photoAddr.city) {
                  photoAddr.city = payload.city || payload.town || payload.village || payload.municipality || '';
                }
                
                // Merge with existing address data
                photos[photoIdx] = {
                  ...photo,
                  address: {
                    ...payload,
                    ...photoAddr, // Keep existing data, prefer it over new
                    country: photoAddr.country || payload.country,
                    country_code: photoAddr.country_code || payload.country_code,
                  }
                };

                // Update step address if needed
                const stepAddr = { ...(step.address || {}) };
                if (!stepAddr.city && photos[photoIdx].address.city) {
                  stepAddr.city = photos[photoIdx].address.city;
                }
                if (!stepAddr.country && photos[photoIdx].address.country) {
                  stepAddr.country = photos[photoIdx].address.country;
                  if (!stepAddr.country_code && photos[photoIdx].address.country_code) {
                    stepAddr.country_code = photos[photoIdx].address.country_code;
                  }
                }

                next[stepIdx] = { ...step, address: stepAddr, photos };
              });

              return next;
            });
          }

          resolved++;
          console.log(`Resolved ${resolved}/${clusters.size} clusters`);
        },
        5 // Process 5 clusters concurrently
      );

      console.log(`City resolution complete: processed ${clusters.size} clusters`);
    } catch (error) {
      console.error('City resolution failed:', error);
      alert('City resolution encountered an error. Some locations may not be resolved.');
    } finally {
      setResolving(false);
    }
  }, [rawSteps, resolving]);

  // LEGACY: Keep old resolve function for manual triggering
  async function resolveAllPlaces() {
    if (resolving) return;
    const needs = rawSteps.flatMap(s => s.photos || []).filter(p => isValidCoord(p?.coordinates) && !p.address);
    if (needs.length === 0) { alert('No GPS-tagged photos need resolving.'); return; }
    if (!window.confirm(`Resolve addresses for ${needs.length} photos? (OpenStreetMap Nominatim)`)) return;

    setResolving(true);
    const cache = new Map();
    const next = rawSteps.map(s => ({ ...s, photos: [...(s.photos || [])] }));

    for (const s of next) {
      for (let i = 0; i < s.photos.length; i++) {
        const p = s.photos[i];
        if (!isValidCoord(p?.coordinates) || p.address) continue;
        const key = `${p.coordinates.lat.toFixed(2)},${p.coordinates.lng.toFixed(2)}`;
        if (!cache.has(key)) {
          const addr = await reverseGeocode(p.coordinates.lat, p.coordinates.lng);
          cache.set(key, addr);
          await new Promise(r => setTimeout(r, 300));
        }
        const addr = cache.get(key);
        if (addr) s.photos[i] = { ...p, address: addr };
      }
      const withAddr = s.photos.find(ph => ph.address);
      s.address = withAddr?.address || s.address || null;
    }

    setRawSteps(next);
    setResolving(false);
  }

  // Background country-only prefill (PRESERVED - no changes to existing country resolution)
  useEffect(() => {
    if (countryPrefetching.current) return;
    
    const todo = rawSteps
      .map((s, idx) => ({ idx, s, c: firstCoord(s) }))
      .filter(x => x.c && !(x.s?.address?.country));

    if (!todo.length) return;
    countryPrefetching.current = true;

    const run = async () => {
      const queue = [...todo];
      const CONCURRENCY = 2;

      async function worker() {
        while (queue.length) {
          const { idx, s, c } = queue.shift();
          const key = `${c.lat.toFixed(2)},${c.lng.toFixed(2)}`;

          let entry = countryCacheRef.current.get(key);
          if (!entry) {
            entry = await reverseCountryOnly(c.lat, c.lng);
            countryCacheRef.current.set(key, entry);
          }
          if (entry?.country) {
            setRawSteps(prev => {
              const next = [...prev];
              if (!next[idx]) return prev;
              // Include state/territory for places like Puerto Rico
              const addr = { 
                ...(next[idx].address || {}), 
                country: entry.country, 
                country_code: entry.country_code,
                ...(entry.state ? { state: entry.state } : {}),  // Add state if present
              };
              next[idx] = { ...next[idx], address: addr };
              return next;
            });
          }
          await new Promise(r => setTimeout(r, 150));
        }
      }

      setTimeout(() => {
        Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker))
          .finally(() => { countryPrefetching.current = false; });
      }, 0);
    };

    run();
  }, [rawSteps]);

  // Export grouped by date
  function exportTimeline() {
    if (!Array.isArray(steps) || steps.length === 0) { alert('Nothing to export yet.'); return; }
    const byDate = new Map();
    steps.forEach(s => {
      const d = s.date || '';
      const place = stepHeaderPlace(s) || s.title || s.location || 'Unknown location';
      if (!byDate.has(d)) byDate.set(d, new Set());
      byDate.get(d).add(place);
    });

    const lines = [];
    Array.from(byDate.entries()).sort((a,b) => a[0].localeCompare(b[0])).forEach(([d, places]) => {
      lines.push(formatUSDate(d));
      lines.push('');
      places.forEach(p => lines.push(p));
      lines.push('');
      lines.push('');
    });

    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'timeline.txt'; a.click();
    URL.revokeObjectURL(url);
  }

  // Make room for Android status bar (prevents overlap) and pick a status bar style.
useEffect(() => {
    if (!(isNativeAndroid() || isNativeIOS())) return;


  // SAFEST: keep WebView below the status bar (prevents overlap)
  StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});

  // Keep icons readable vs your theme
  StatusBar.setStyle({ style: theme === 'dark' ? Style.Dark : Style.Light }).catch(() => {});

  // Optional: match your topbar color (helps during scroll)
  // StatusBar.setBackgroundColor({ color: '#0b142a' }).catch(() => {});
}, [theme]);


// Browser tab title
useEffect(() => {
  document.title = 'VivaJour';
}, []);

// Sync CSS theme class with state so tokens in index.css switch correctly
useEffect(() => {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}, [theme]);

// 🔹 NEW: Handle Web Browser Back Button
  useEffect(() => {
    // Only activate this trap if we are in the editor view
    if (view === 'past') {
      // 1. Push a dummy state to history so "Back" has somewhere to go
      window.history.pushState({ page: 'editor' }, '', '');

      const onPopState = (event) => {
        // 2. User clicked Back. Prevent leaving by pushing state again immediately.
        window.history.pushState({ page: 'editor' }, '', '');
        
        // 3. Go back to start
        setView('start');
      };

      window.addEventListener('popstate', onPopState);
      return () => window.removeEventListener('popstate', onPopState);
    }
  }, [view]);

// Handle lightweight, link-only viewer for shared albums
if (typeof window !== 'undefined' && window.location.pathname === '/share') {
  return <ShareViewer />;
}

  const openAlbumFromEntry = async (album) => {
    try {
      // 1. Load the Trip (Photos/Steps)
      let trip = album.trip;
      if (!trip || !trip.steps) {
        try { trip = await getTrip(album.id); } catch (e) {}
      }
      if (!trip) { alert('Trip data missing.'); return; }

      // Restore Trip Data
      setTripName(trip.name || '');
      setRawSteps(trip.steps || []);
      setCurrentTripId(trip.id);
      setLastSavedAt(trip.savedAt);
      setIsNameLocked(true);

      // Reset autosave flags
      setIsAutoSaved(false);
      setAutoSavedId(null);

      // 2. 🔹 NEW: Load the Album Configuration
      const albumData = album.albumData || trip.albumData;
      
      if (albumData) {
        console.log('[OpenAlbum] Loading saved album configuration...');
        setLoadedAlbumData(albumData); // <--- Store data in state
      } else {
        setLoadedAlbumData(null);      // <--- No data, start fresh
      }

      // 3. Open the overlay on top of 'past' view
      setShowAlbumList(false);
      setView('past');
      setAlbumFromPastOpen(true);

    } catch (e) {
      console.error('[OpenAlbum] Failed:', e);
      alert('Failed to open album.');
    }
  };


  // ------------------------------ Views ------------------------------
  // ------------------------------ Views ------------------------------
if (view === 'start') {
  return (
    <div className="landing-wrap">
      {/* Header with logo + wordmark */}
      <header className="landing-header">
  <div className="landing-brand-stack">
    <img
      src={vivaJourLogo}
      alt="VivaJour app icon"
      className="landing-logo-mark"
    />
    <img
      src={vivaJourWordmark}
      alt="VivaJour wordmark"
      className="landing-wordmark"
    />
  </div>
  {/* ✅ NEW: Account Menu */}
  <div style={{ position: 'absolute', top: isNativeAndroid() ? 40 : 16, right: 16 }}>
    <AccountMenu />
  </div>
</header>



      {/* Main hero content */}
      <main className="landing-content">
        {/* LEFT: Text + primary actions */}
        <div className="landing-text-col">
          <h1 className="landing-title">
            Don't Just Take Photos. Live the Journey.
          </h1>
          <p className="landing-subtitle">
            VivaJour transforms your travel photos into a vivid, story-driven digital album—automatically.
          </p>

          <div className="landing-actions">
            {/* 1. Create My Story - Primary Action */}
            <button
              type="button"
              className="btn-landing-primary"
              onClick={startNewTrip}
            >
              <span className="btn-label-main">
                <span role="img" aria-hidden="true">✨</span>
                <span>+ Create My Story</span>
              </span>
              <span className="btn-label-sub">
                Transform your trip photos into a beautiful album
              </span>
            </button>

            {/* 2. Current Trip (Daily Digest) */}
            <button
              type="button"
              className="btn-landing-primary"
              onClick={() => {
                setDigestMode('current');
                setShowDigestDialog(true);
              }}
            >
              <span className="btn-label-main">
                <span role="img" aria-hidden="true">⭐</span>
                <span>Current Trip (Daily Digest)</span>
              </span>
              <span className="btn-label-sub">
                Chronicle your current journey, day by day, or all at once
              </span>
            </button>

            {/* Secondary links */}
            <div className="landing-secondary-row">
              <button
                type="button"
                className="btn-link-subtle"
                onClick={() => setShowTripList(true)}
              >
                <span>📚</span>
                <span>My Trips</span>
              </button>

              <button
                type="button"
                className="btn-link-subtle"
                onClick={() => setShowAlbumList(true)}
              >
                <span>🎞</span>
                <span>My Albums</span>
              </button>

              <button
                type="button"
                className="btn-link-subtle"
                onClick={() => setShowHelp(true)}
              >
                <span>❓</span>
                <span>Help & FAQ</span>
              </button>
            </div>

            {/* Trust strip - clickable links to landing pages */}
            <div className="landing-trust">
              <Link to="/privacy-first" className="trust-link">
                ✓ 100% private – processed on your device
              </Link>
              <Link to="/how-it-works" className="trust-link">
                ✓ No signup required to try
              </Link>
            </div>
          </div>
        </div>

        {/* RIGHT: Visual hero */}
        <div className="landing-visual-col">
          <div className="bg-slate-900/40 rounded-3xl p-6 md:p-8 border border-slate-700/60 shadow-2xl shadow-sky-900/40">
            {/* For now, use the logo as the hero visual.
                Later we can swap this src for a 3D book image asset. */}
            
            <div className="landing-hero-glow"></div>
            <img
  src={heroImage}
  alt="VivaJour travel story preview"
  className="landing-book-img"
/>


          </div>
        </div>
      </main>

      {/* Trip List Modal (available on Start view) */}
      {showTripList && (
        <TripList
          onLoadTrip={loadTripFromStorage}
          onClose={() => setShowTripList(false)}
          onNewTrip={startNewTrip}
        />
      )}

      {/* Album List Modal (available on Start view) */}
      <MyAlbumsModal
        open={showAlbumList}
        onClose={() => setShowAlbumList(false)}
        onOpenAlbum={openAlbumFromEntry}
      />

      {/* Daily Digest dialog (global for start view) */}
      <CurrentTripDigestDialog
        open={showDigestDialog}
        onClose={() => {
          setShowDigestDialog(false);
          setDigestMode(null);  // Reset digestMode on cancel
        }}
        onConfirm={({ selectedDates, journalText, tripTitle }) => {
          if (!selectedDates || selectedDates.length === 0) {
            alert('Please select at least one day for your Daily Digest.');
            return;
          }

          if (digestMode === 'current') {
            // 4) Kick off import
            if (isNativeAndroid() || isNativeIOS()) {
              // ═══════════════════════════════════════════════════════════════════════
              // 🛠️ CRITICAL FIX: Show loader FIRST, before any other state changes!
              // The loader must be visible BEFORE dialog closes or view changes.
              // ═══════════════════════════════════════════════════════════════════════
              console.log('[CurrentTrip] iOS/Android: Setting up loader FIRST');
              setDigestFlowActive(true);
              setShowGatheringLoader(true);
              setSuppressLoader(true);
              setGatheringPhotos([]);
              
              // Store config for after import
              setPendingDigestConfig({ selectedDates, journalText, tripTitle });
              setPendingDigestAfterImport(true);
              
              // Close dialog AFTER loader is set up
              setShowDigestDialog(false);
              
              // Start new trip LAST (this changes view to 'past')
              startNewTrip();
              
              // Defer picker until UI has painted the loader
              clearCurrentTripPickTimer();
              currentTripPickTimerRef.current = setTimeout(async () => {
                currentTripPickTimerRef.current = null;
                try {
                  console.log('[CurrentTrip] Opening native picker...');
                  const ok = await handleNativePick({ selectedDates, journalText, tripTitle });
                  if (!ok) {
                    console.log('[digest] Native picker cancelled/failed');
                    hardResetCurrentTripFlow();
                  }
                } catch (err) {
                  console.error('[digest] Native picker error:', err);
                  hardResetCurrentTripFlow();
                }
              }, 100);  // Reduced from 350ms - just need paint time
            } else {
              // Web flow: same order - loader first
              setDigestFlowActive(true);
              setShowGatheringLoader(true);
              setSuppressLoader(true);
              setGatheringPhotos([]);
              
              setPendingDigestConfig({ selectedDates, journalText, tripTitle });
              setPendingDigestAfterImport(true);
              setShowDigestDialog(false);
              startNewTrip();
              
              setResolvingProgress(5);
              console.log('[CurrentTrip] Web flow - showing loader');
              // useEffect handles file input click
            }
          } else {
            // Non-current flows: run digest immediately on the current trip
            handleGenerateDailyDigest({ selectedDates, journalText, tripTitle });
          }
        }}
      />

      {/* ===== Footer with Landing Page Links ===== */}
      <footer className="landing-footer-strip">
        <div className="landing-footer-links-row">
          <Link to="/privacy-first" className="landing-footer-link">Privacy</Link>
          <Link to="/how-it-works" className="landing-footer-link">How It Works</Link>
          <Link to="/current-trip" className="landing-footer-link">Current Trip</Link>
          <Link to="/albums" className="landing-footer-link">Albums</Link>

          <Link to="/templates" className="landing-footer-link">Templates</Link>
          <Link to="/privacy-policy" className="landing-footer-link">Privacy Policy</Link>
<Link to="/terms" className="landing-footer-link">Terms</Link>
        </div>
        <p className="landing-footer-copy">
          © 2025 VivaJour Labs LLC • Built in Wisconsin
        </p>
      </footer>

      {/* Help & FAQ Modal (available on Start view) */}
      {showHelp && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            zIndex: 2000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 16,
          }}
        >
          <div
            className="card"
            style={{
              maxWidth: 840,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <div className="title">Help &amp; FAQ</div>
              <button
                className="btn btn-ghost"
                onClick={() => setShowHelp(false)}
                title="Close help"
              >
                ✕
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4 }}>
              <FAQContent />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}




  // Album view route — now mounts the new Album Generator
  // Fallback: route old album view back to Past Trips
  //if (view === 'album') {
   // return (
     // <div className="wrap">
     //   <header className="topbar status-safe">
      //  <button className="logo" onClick={() => setView('start')} title="Back to start">
      //    {isNameLocked && tripName ? tripName : 'Travel Tracker'}
      //  </button>
     //   <div className="spacer" />
     //   </header>
       // <div className="content">
         // <AlbumGenerator />
       // </div>
     // </div>
   // );
//  }

  if (view !== 'past') {
    return (
      <div className="wrap">
        <header className="topbar status-safe">
  <button
    className="logo"
    onClick={() => setView('start')}
    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
  >
    <span
      aria-hidden="true"
      style={{
        fontSize: 20,
        lineHeight: 1,
        marginRight: 2,
        opacity: 0.95,
        transform: 'translateY(-1px)',
      }}
    >
      ←
    </span>
    
    <img
      src={vivaJourLogo}
      alt="VivaJour logo"
      style={{ height: 28, borderRadius: 8 }}
    />
    <span>VivaJour</span>
  </button>
  <div className="spacer" />
  <button className="btn" onClick={() => setView('past')}>Go to Past</button>
</header>

        <div className="content">
          <div className="card"><div className="title">Coming soon</div></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`wrap ${theme}`}>
      <header className="topbar status-safe">
  <button
    className="logo"
    onClick={() => setView('start')}
    title="Back to start"
    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
  >

    <span
      aria-hidden="true"
      style={{
        fontSize: 20,
        lineHeight: 1,
        marginRight: 2,
        opacity: 0.95,
        transform: 'translateY(-1px)',
      }}
    >
      ←
    </span>
    <img
      src={vivaJourLogo}
      alt="VivaJour logo"
      style={{ height: 28, borderRadius: 8 }}
    />
    <span>
      {isNameLocked && tripName ? tripName : 'VivaJour'}
    </span>
  </button>
  <div className="spacer" />
  <button
    className="btn btn-ghost"
    onClick={() => setTheme(t => (t === 'dark' ? 'light' : 'dark'))}
    title="Toggle theme"
  >
    {theme === 'dark' ? '☀️' : '🌙'}
  </button>
  <button
    className="btn btn-ghost"
    onClick={() => setShowHelp(true)}
    title="Open help and FAQ"
    style={{ marginLeft: 8 }}
  >
    Help &amp; FAQ
  </button>

  {/* ✅ NEW: Account Menu */}
  <div style={{ marginLeft: 8 }}>
    <AccountMenu />
  </div>

  <button 
    className="btn" 
    onClick={() => setAlbumFromPastOpen(true)}
    disabled={geocodingInProgress}
    title={geocodingInProgress ? 'Please wait for location resolution to complete' : 'Create album from this trip'}
    style={{ display: 'none' }} // keep your existing behavior
  >
    {geocodingInProgress ? 'Resolving locations...' : 'Create album'}
  </button>
</header>

{showHelp && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            zIndex: 2000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 16,
          }}
        >
          <div
            className="card"
            style={{
              maxWidth: 840,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <div className="title">Help &amp; FAQ</div>
              <button
                className="btn btn-ghost"
                onClick={() => setShowHelp(false)}
                title="Close help"
              >
                ✕
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4 }}>
              <FAQContent />
            </div>
          </div>
        </div>
      )}


      <button
  className="btn"
  onClick={() => setShowTripList(true)}
  title="Open saved trips"
  style={{ marginLeft: 8 }}
>
  📚 My Trips
</button>


      <div className="content two-col">
        {/* Left: controls + steps */}
        <div className="left-col">
          {/* Collapsible Controls */}
          {controlsCollapsed ? (
            <div className="card" style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 700 }}><span style={{fontSize: 26, fontWeight: 800}}>{tripName || 'Trip tools'}</span></div>
              <button className="btn" onClick={() => setControlsCollapsed(false)} title="Show trip tools">▾ Show</button>
            </div>
          ) : (
            <div className="card controls">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ margin: 0, flex: 1, fontSize: 22, fontWeight: 800 }}>
                  {isNameLocked && tripName ? tripName : (tripName || 'Name your trip')}
                </div>
                <button className="btn" onClick={() => setControlsCollapsed(true)} title="Hide trip tools">▴ Hide</button>
              </div>

              <div className="grid2" style={{ marginTop: 8 }}>
                <div className="row">
                  <label className="trip-name-label">
  Trip name
  <input
    className="trip-name-input"
    value={tripName}
    onChange={e => setTripName(e.target.value)}
    placeholder="e.g. Summer in Spain"
  />
  <div className="trip-name-hint">
    This name will be saved and used for your album and timeline
  </div>
</label>

                  {isNameLocked && (
                    <button className="btn btn-ghost" title="Edit name" onClick={() => setIsNameLocked(false)}>
                      ✎
                    </button>
                  )}
                </div>
                <span />
              </div>

              <div className="uploader" style={{ marginTop: 8 }}>
  {/* Date Filter - integrated into photo selection */}
  {/* Date filter - hidden for now (no performance benefit in current implementation)
  <div style={{ 
    marginBottom: 16, 
    padding: 12, 
    background: 'rgba(0,0,0,0.05)', 
    borderRadius: 8,
    border: '1px solid rgba(0,0,0,0.1)'
  }}>
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, marginBottom: 8, cursor: 'pointer' }}>
      <input 
        type="checkbox" 
        checked={dateFilterEnabled} 
        onChange={e => setDateFilterEnabled(e.target.checked)} 
      />
      <span style={{ fontWeight: 500 }}>📅 Filter photos by date range</span>
    </label>
    
    {dateFilterEnabled && (
      <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
        <label style={{ flex: 1, fontSize: 13 }}>
          <div style={{ marginBottom: 4, opacity: 0.7 }}>From</div>
          <input
            type="date"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid rgba(0,0,0,0.2)' }}
          />
        </label>
        <label style={{ flex: 1, fontSize: 13 }}>
          <div style={{ marginBottom: 4, opacity: 0.7 }}>To</div>
          <input
            type="date"
            value={toDate}
            onChange={e => setToDate(e.target.value)}
            style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid rgba(0,0,0,0.2)' }}
          />
        </label>
      </div>
    )}
  </div>
  */}

  {/* Hidden input for web fallback */}
  <input
    id="fileInput"
    type="file"
    multiple
    accept="image/*,.heic,.heif"
    style={{ display: 'none' }}
    onChange={e => onFilesSelected(Array.from(e.target.files || []))}
  />

  <button
  type="button"
  className="btn btn-cta-blue"
  onClick={async () => {
  // Just try the native picker - GalleryActivity handles permissions itself
  // System permission dialog will appear if needed; no custom modal
  const ok = await handleNativePick();

  if (!ok) {
    if (isNativeAndroid()) {
      console.log('[choose] Android picker cancelled/failed - user will need to try again');
      return;
    }
    if (isNativeIOS()) {
      console.log('[choose] iOS picker returned empty/cancelled');
      return;
    }
    // Web fallback
    const el = document.getElementById('fileInput');
    if (el) el.click();
  }
}}
  title="Choose photos"
>
  Choose photos
</button>

  {/* NEW: Folder import button */}
  <button
    type="button"
    className="btn btn-ghost"
    onClick={handleFolderImport}
    title="Import all photos from a folder"
  >
    Choose folder
  </button>
</div>


              {/* Progress */}
              {progress.total > 0 && (
                <div className="progress">
                  <div className="progress-bar" style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }} />
                  <div className="progress-label">Importing {progress.done}/{progress.total}</div>
                </div>
              )}

              <div className="actions">
                <button
                  className="btn btn-primary"
                  onClick={saveTripNow}
                  disabled={!tripName || rawSteps.length === 0}
                >
                  Save trip
                </button>

                <button className="btn btn-ghost" onClick={exportTimeline}>Export timeline</button>
              </div>

              {lastSavedAt && (
                <div style={{ fontSize: 12, opacity: .7, marginTop: 6 }}>
                  Saved {new Date(lastSavedAt).toLocaleString()}
                </div>
              )}
            </div>
          )}

          {/* Steps */}
          <div className="stack">
            {steps.map((s, i) => {
              const c = firstCoord(s);
              const collapsed = !expandedSteps.has(i);
              const visibleCount = gridVisibleCounts[i] || 0;
              const totalCount = (s.photos?.length || 0);
              const showAllDisabled = collapsed ? (visibleCount >= totalCount) : false;
              return (
                <div key={s.id || i} id={`step-${i}`} className="card step">
                  <StepHeaderCompact
                    step={s}
                    onEdit={() => setEditing({ key: (s._key || s.id), step: s })}
                    onShowAll={() =>
                      setExpandedSteps(prev => {
                        const next = new Set(prev);
                        if (next.has(i)) next.delete(i); else next.add(i);
                        return next;
                      })
                    }
                    ShowAllButton={
                      totalCount > 0 ? (
                        <button
                          className="btn btn-ghost"
                          onClick={() =>
                            setExpandedSteps(prev => {
                              const next = new Set(prev);
                              if (next.has(i)) next.delete(i); else next.add(i);
                              return next;
                            })
                          }
                          disabled={showAllDisabled}
                          title={showAllDisabled ? 'All photos visible' : undefined}
                        >
                          {collapsed ? 'Show all' : 'Collapse'}
                        </button>
                      ) : null
                    }
                  />

                  <div className="step-sub" style={{ fontSize: 12, opacity: .75 }}>
                    {c ? '' : '📍 no GPS on this step yet'}
                  </div>

                  <StepPhotosGrid
                    photos={s.photos || []}
                    collapsed={collapsed}
                    onLike={(photo) => likePhotoByKey((s._key || s.id), photo)}
                    onDelete={(photo) => deletePhotoByKey((s._key || s.id), photo)}
                    onVisibleCount={(n) => setGridVisibleCounts(prev => (prev[i] === n ? prev : { ...prev, [i]: n }))}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: map */}
        <div className="right-col">
          <MapPanel steps={steps} onSelectStep={scrollToStep} />
        </div>
      </div>

      {/* Open the wizard; "Continue" will push into the Album Generator page */}
      <AlbumWizardModal open={albumOpen} onClose={() => setAlbumOpen(false)} onCreate={() => { setAlbumOpen(false); setView('past'); }} />
      
{/* Resolving Loader - shows during import, geocoding, AND album generation */}
      {/* Resolving Loader - shows during import, geocoding, AND album generation */}
{FEATURE_FLAGS.SHOW_RESOLVING_LOADER && (
  <>
    {/* 🔹 Beautiful Photo Gathering Animation — CURRENT TRIP ONLY */}
    {/* 🔹 FIX JITTER: Conditionally render to force full unmount/remount cycle */}
    {/* This ensures animation state is completely fresh each time */}
    {(isGatheringActive || isPastTripGatheringActive) && (
      <AlbumGatheringLoader
        key={`gathering-${gatheringSessionId}`}  // Extra safety: force remount on session change
        isVisible={true}  // Always visible when mounted
        photos={isPastTripGatheringActive ? pastTripGatheringPhotos : gatheringPhotos}
        progress={resolvingProgress}
        onComplete={() => {
  console.log('[AlbumGatheringLoader] Animation complete - cleaning up');
  
  // NOW do the cleanup - after the loader's exit animation finished
  hardResetCurrentTripFlow();
  
  // Past Trip cleanup
  setPastTripGenerating(false);
  setPastTripGatheringPhotos([]);
  
  // Clean up ResolvingLoader state
  setShowResolvingLoader(false);
  setLoaderContext(null);
  setResolvingStatus('');
  setResolvingProgress(0);
}}
        onCancel={() => {
          console.log('[AlbumGatheringLoader] Cancelled by user');
          // 🔹 FIX: Full cleanup on cancel using centralized helper
          hardResetCurrentTripFlow();

          // Past Trip cleanup (separate from Current Trip flow)
          if (pastTripGenerating) {
            setPastTripGenerating(false);
            setPastTripGatheringPhotos([]);
          }
          
          setShowResolvingLoader(false);
          setLoaderContext(null);
          setResolvingProgress(0);
          setResolvingStatus('');
        }}
      />
    )}
    
    {/* Original ResolvingLoader for non-digest flows (e.g. manual Past Trip / "Create My Story") */}
    <ResolvingLoader
      isVisible={
        !isGatheringActive &&                // ⬅️ ONLY hide when Current Trip hero is active
        !suppressLoader &&
        showResolvingLoader &&
        (loaderContext === 'import' || loaderContext === 'geocoding' || loaderContext === 'generating')
      }
      progress={resolvingProgress}
      statusMessage={resolvingStatus}
      onCancel={cancelResolvingFlow}
      onComplete={() => {
        // let the success state breathe; then hide and clear context
        setTimeout(() => {
          setShowResolvingLoader(false);
          setLoaderContext(null);
        }, 300);
      }}
    />
  </>
)}



      {/* Trip List Modal */}
{/* Trip List Modal */}
{showTripList && (
  <TripList
    onLoadTrip={loadTripFromStorage}
    onClose={() => setShowTripList(false)}
    onNewTrip={startNewTrip} 
  />
)}

{/* 🔹 Album List Modal */}
            {/* 🔹 Album List Modal */}
      <MyAlbumsModal
        open={showAlbumList}
        onClose={() => setShowAlbumList(false)}
        onOpenAlbum={openAlbumFromEntry}
      />


      <AlbumFromPast
      // 🔹 NEW: Force a fresh mount when trip changes
        key={digestMeta ? `digest-${digestMeta.timestamp}` : (currentTripId || 'new-trip')}
        open={albumFromPastOpen}
        //steps={rawSteps}               // full, un-limited steps so user can exclude
        steps={stepsForAlbum}          // use normalized steps so album has real location labels
        tripName={tripName}            // ← pass the name from App state
        resolving={resolving}          // CHANGE #1: Pass geocoding status to component.
        
        digestMeta={digestMeta}           // 🔹 NEW

        // 🔹 NEW: Show cinematic loader when Past Trip generates album
onGenerateStart={(photos) => {
  console.log('[onGenerateStart] Past Trip generation starting, photos:', photos?.length);
  setGatheringSessionId(prev => prev + 1); // 🔹 FIX JITTER: Force fresh animation mount
  setPastTripGenerating(true);
  setPastTripGatheringPhotos(photos || []);
  // 🔹 FIX: Keep flying-photo loader visible until AlbumFromPast signals it is rendered
  setAwaitingAlbumReady(true);
setAwaitingAlbumReadySource("past");
}}


        // 🔹 NEW: Pass the saved data here!
                // 🔹 NEW: Pass the saved data here!
        // For Current Trip digest we always start fresh, so ignore any previous initialData
        initialData={(digestMode === 'current' || digestMeta) ? null : loadedAlbumData}

        currentTripId={currentTripId}  


        onClose={() => {
          console.log('[AlbumFromPast onClose] Cleaning up all loader states...');
          // 🔹 FIX: Full cleanup using centralized helper
          hardResetCurrentTripFlow();
          setAlbumFromPastOpen(false);
          
          // Reset Past Trip states too
          setPastTripGenerating(false);
          setPastTripGatheringPhotos([]);
        }}

        // 🔹 NEW: Called when album generation completes - hide the loader
                // 🔹 NEW: Called when album generation completes - always safely hide the loader
          // ⬇️ REPLACEMENT: Optimized onAlbumReady with MINIMUM DURATION enforcement ⬇️
        onAlbumReady={() => {
  console.log('[onAlbumReady] Album generation complete - triggering loader exit');
  setResolvingProgress(100); // This triggers the loader's internal 2-second exit sequence
  // Don't do cleanup here - let onComplete handle it
}}


        // ⬇️ UPDATED HANDLER ⬇️
                onGenerate={async (payload) => {
          try {
            // 1) Stash choices to local storage (legacy backup)
            try {
              localStorage.setItem('tt:album:fromPast', JSON.stringify(payload));
            } catch {}

            // 2) Auto-save and GET THE ID
            // This saves the trip AND albumData into IndexedDB via tripStorage.
            await performAutoSave(rawSteps, payload);

            // 3) Close the overlay and stay on the Past Trip view.
            // 🔹 IMPORTANT:
            // We do NOT set any vvd:returnContext here anymore.
            // vvd:returnContext is now only set when the user actually opens preview.
            setAlbumFromPastOpen(false);
            setView('past');
          } catch (e) {
            console.error('Generate error:', e);
          }
        }}

      />

      <CurrentTripDigestDialog
  open={showDigestDialog}
  onClose={() => {
    setShowDigestDialog(false);
    setDigestMode(null);  // 🔹 FIX: Reset digestMode on cancel to prevent stale state
  }}
  onConfirm={async ({ selectedDates, journalText, tripTitle }) => {

  if (!selectedDates || selectedDates.length === 0) {
    alert('Please select at least one day for your Daily Digest.');
    return;
  }

  if (digestMode === 'current') {
    // 4) Kick off import:
    if (isNativeAndroid() || isNativeIOS()) {

// 🔹 FIX: New session - invalidate any stale updates
      currentFlowSessionRef.current += 1;
      setResolvingProgress(0); // Reset progress for fresh start


      // ═══════════════════════════════════════════════════════════════════════
      // 🛠️ CRITICAL FIX: Show loader FIRST, before any other state changes!
      // The loader must be visible BEFORE dialog closes or view changes.
      // ═══════════════════════════════════════════════════════════════════════
      console.log('[CurrentTrip] iOS/Android: Setting up loader FIRST');
      
      // 🔹 FIX JITTER: Clean slate - clear everything before setting new state
      setGatheringPhotos([]); // Clear old photos FIRST
      gatheringLoaderVisibleAtRef.current = null; // Reset visibility timing
      setGatheringSessionId(prev => prev + 1); // Force fresh animation mount
      
      // Now set up the loader
      setDigestFlowActive(true);
      setShowGatheringLoader(true);
      setSuppressLoader(true);
      
      // 🔹 FIX LOADER DISAPPEARING: Set awaitingAlbumReady EARLY to prevent any gap
      // This ensures isGatheringActive stays true even during state transitions
      setAwaitingAlbumReady(true);
      setAwaitingAlbumReadySource("digest");
      
      // Store config for after import
      setPendingDigestConfig({ selectedDates, journalText, tripTitle });
      setPendingDigestAfterImport(true);
      
      // Close dialog AFTER loader is set up
      setShowDigestDialog(false);
      
      // Start new trip LAST (this changes view to 'past')
      startNewTrip();
      
      // Defer picker until UI has painted the loader
      clearCurrentTripPickTimer();
      currentTripPickTimerRef.current = setTimeout(async () => {
        currentTripPickTimerRef.current = null;
        try {
          console.log('[CurrentTrip] Opening native picker...');
          const ok = await handleNativePick({ selectedDates, journalText, tripTitle });
          if (!ok) {
            console.log('[digest] Native picker cancelled/failed');
            hardResetCurrentTripFlow();
          }
        } catch (err) {
          console.error('[digest] Native picker error:', err);
          hardResetCurrentTripFlow();
        }
      }, 100);  // Reduced from 350ms - dialog is already closed, just need paint time
    } else {
      // Web flow: same order - loader first
      // 🔹 FIX JITTER: Clean slate - clear everything before setting new state
      setGatheringPhotos([]); // Clear old photos FIRST
      gatheringLoaderVisibleAtRef.current = null; // Reset visibility timing
      setGatheringSessionId(prev => prev + 1); // Force fresh animation mount
      
      // Now set up the loader
      setDigestFlowActive(true);
      setShowGatheringLoader(true);
      setSuppressLoader(true);
      
      // 🔹 FIX LOADER DISAPPEARING: Set awaitingAlbumReady EARLY to prevent any gap
      // This ensures isGatheringActive stays true even during state transitions
      setAwaitingAlbumReady(true);
      setAwaitingAlbumReadySource("digest");
      
      setPendingDigestConfig({ selectedDates, journalText, tripTitle });
      setPendingDigestAfterImport(true);
      setShowDigestDialog(false);
      startNewTrip();
      
      setResolvingProgress(5);
      console.log('[CurrentTrip] Web flow - showing loader');
      // useEffect handles file input click
    }
  } else {
    // Non-current flows: run digest immediately on the current trip
    handleGenerateDailyDigest({ selectedDates, journalText, tripTitle });
  }
}}

/>

      <StepEditorModal
        open={!!editing}
        step={editing?.step}
        onChange={(draft) => updateStep(editing.key, draft)}
        onClose={() => setEditing(null)}
      />

      {/* Light/Dark minimal theme overrides */}
      <style>{`
        .wrap.light { background: #f3f7fb; color: #0f172a; }
        .wrap.light .card { background: #ffffff; color: #0f172a; }
        .wrap.light .topbar { background: rgba(255,255,255,0.85); }
        .wrap.light .btn.btn-ghost { color: #0f172a; }
      `}</style>

      <div style={{ height: 24 }} />
    </div>
  );
}



// Back button → route-aware behavior for Android
function BackButtonHandler() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    console.log('[BackButtonHandler] Initializing...');
    console.log('[BackButtonHandler] Platform:', Capacitor.getPlatform());
    
    // Only attach on native (Android/iOS), not web
    if (Capacitor.getPlatform() === 'web') {
      console.log('[BackButtonHandler] Skipping - running on web');
      return;
    }

    console.log('[BackButtonHandler] Registering for path:', location.pathname);

    let subscription;
    let isCleanedUp = false;

    // CAPACITOR 7: addListener returns a Promise!
    // We must handle the Promise to get the actual subscription object
    CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      console.log('🔙 [BackButton] Hardware back button pressed!');
      console.log('[BackButton] Current path:', location.pathname);
      console.log('[BackButton] Can go back in history:', canGoBack);
      
      // 1) Let the app state try to handle back (close modals / pop internal views)
      const ev = new CustomEvent('app:hardwareBack', { cancelable: true });
      const notCancelled = window.dispatchEvent(ev);
      console.log('[BackButton] Event dispatched, was it handled?', !notCancelled);
      
      if (!notCancelled) {
        console.log('[BackButton] ✅ Event was handled by app state');
        return;
      }

      // 2) Fallback: route-aware behavior (story/share → go back in history)
      const p = location.pathname || '/';
      
      if (p.startsWith('/story') || p.startsWith('/share')) {
        console.log('[BackButton] ✅ On story/share route, navigating back');
        navigate(-1);
        return;
      }

      // 3) Final fallback: if there's real history, go back; otherwise stay
      console.log('[BackButton] Checking history - length:', window.history.length);
      if (window.history.length > 1) {
        console.log('[BackButton] ✅ Going back in history');
        window.history.back();
      } else {
        console.log('[BackButton] ⚠️ At root, staying in app');
        // Don't minimize - just stay in the app
      }
    }).then(sub => {
      if (isCleanedUp) {
        // Component unmounted before Promise resolved
        console.log('[BackButtonHandler] Component unmounted, removing listener immediately');
        sub.remove();
        return;
      }
      
      subscription = sub;
      console.log('[BackButtonHandler] ✅ Listener registered successfully (Capacitor 7)');
      console.log('[BackButtonHandler] Subscription type:', typeof subscription);
      console.log('[BackButtonHandler] Has remove method:', typeof subscription?.remove === 'function');
    }).catch(err => {
      console.error('[BackButtonHandler] ❌ Failed to register listener:', err);
    });

    return () => {
      console.log('[BackButtonHandler] Cleanup called');
      isCleanedUp = true;
      if (subscription) {
        console.log('[BackButtonHandler] Removing listener');
        subscription.remove();
      } else {
        console.log('[BackButtonHandler] No subscription to remove (Promise may not have resolved yet)');
      }
    };
  }, [location.pathname, navigate]);

  return null;
}

function IOSRouteCloseShell({ title = 'VivaJour', children }) {
  const navigate = useNavigate();

  // Only show this chrome in the native iOS app
  if (!isNativeIOS()) return children;

  const handleClose = () => {
    // If there is navigation history, go back; otherwise go home.
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0b142a' }}>
      <div
        className="status-safe"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 12px',
          background: 'rgba(11, 20, 42, 0.92)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <div style={{ fontWeight: 700, color: '#fff', fontSize: 14 }}>
          {title}
        </div>

        <button
          className="btn btn-ghost"
          onClick={handleClose}
          title="Close"
          style={{
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.18)',
          }}
        >
          Close
        </button>
      </div>

      <div style={{ minHeight: 'calc(100vh - 56px)' }}>
        {children}
      </div>
    </div>
  );
}



// Router wrapper for Story Player
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SubscriptionProvider>
          <BackButtonHandler />  {/* ← add this line */}
          <SubscriptionSuccessHandler />  {/* ← ADD THIS LINE */}
          
          <Routes>
            {/* Story Player - Auto-playing story mode */}
            <Route
              path="/story"
              element={
                <IOSRouteCloseShell title="Story Preview">
                  <StoryViewer />
                </IOSRouteCloseShell>
              }
            />

            <Route
              path="/share/:id"
              element={
                <IOSRouteCloseShell title="Album Preview">
                  <ShareViewer />
                </IOSRouteCloseShell>
              }
            />

            
            {/* Test route */}
            <Route path="/story-test" element={<StoryPlayerTest />} />

            {/* Firebase Diagnostic */}
            

            <Route path="/diagnostic" element={<DiagnosticPage />} />
            <Route path="/test-share" element={<ShareTestGenerator />} />
            
            <Route path="/test-share" element={<ShareTestGenerator />} />

            {/* ===== Landing Pages (SEO) ===== */}
            <Route path="/landing" element={<LandingHome />} />
            <Route path="/privacy-first" element={<PrivacyFirstPage />} />
            <Route path="/current-trip" element={<CurrentTripPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/albums" element={<AlbumsPage />} />
            <Route path="/templates" element={<TemplatesPage />} />

            <Route
              path="/technical-details"
              element={<TechnicalDetailsPage />}
            />

            
            {/* ===== Legal Pages ===== */}
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />

            {/* Main app */}
            <Route path="*" element={<AppContent />} />
          </Routes>
        </SubscriptionProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}